// Ultimate Card Game (UCG) - Xeno Swamp Battlefield Background
//
// Third battlefield backdrop (js/engine/maps.js selects between 'human',
// 'sul', and 'swamp'). Ported from the standalone art prototype at
// sandbox/xenoswamp-map/ into the real renderer's contract:
// renderSwampStaticBackground and renderSwampStaticForeground are baked
// once into offscreen world-space canvases by Renderer.ensureStaticBackground(),
// while renderSwampDynamicBackground, renderSwampDynamicForeground,
// renderSwampForegroundCritters, and weather methods run every frame.

import { getSwampFogIntensity } from '../maps.js';

const WIDTH = 1600;
const HEIGHT = 540;
const LANE_Y = 176;
const LANE_H = 320;
const FLOOR_ROW_H = LANE_H / 10; // 32
const FLOOR_TOP_Y = LANE_Y + FLOOR_ROW_H; // 208
const SKY_BOTTOM = FLOOR_TOP_Y + 2; // 210
const LANE_BOTTOM = LANE_Y + LANE_H; // 496
const SHELF_H = HEIGHT - LANE_BOTTOM; // 44

function resolveBounds(renderer, customBounds) {
  const h = (renderer && typeof renderer.height === 'number') ? renderer.height : 540;
  const w = (renderer && typeof renderer.width === 'number') ? renderer.width : 1600;
  const bounds = customBounds || (renderer && renderer.viewport ? renderer.viewport.getVisibleBounds() : null);
  const rawTop = bounds ? (typeof bounds.top === 'number' ? bounds.top : (typeof bounds.visibleTop === 'number' ? bounds.visibleTop : -40)) : -40;
  const rawBottom = bounds ? (typeof bounds.bottom === 'number' ? bounds.bottom : (typeof bounds.visibleBottom === 'number' ? bounds.visibleBottom : (h + 40))) : (h + 40);
  const rawLeft = bounds ? (typeof bounds.left === 'number' ? bounds.left : (typeof bounds.visibleLeft === 'number' ? bounds.visibleLeft : -40)) : -40;
  const rawRight = bounds ? (typeof bounds.right === 'number' ? bounds.right : (typeof bounds.visibleRight === 'number' ? bounds.visibleRight : (w + 40))) : (w + 40);
  return {
    visibleTop: customBounds ? rawTop : Math.min(0, rawTop - 20),
    visibleBottom: customBounds ? rawBottom : Math.max(h, rawBottom + 20),
    visibleLeft: customBounds ? rawLeft : Math.min(0, rawLeft - 20),
    visibleRight: customBounds ? rawRight : Math.max(w, rawRight + 20)
  };
}

function getQualityScale(quality) {
  if (quality === 'low') return 0;
  if (quality === 'medium') return 0.5;
  return 1;
}

// Terrain elevation contour functions
export function getFarRidgeY(x) {
  return 165 + Math.sin(x * 0.0032 + 0.8) * 10 + Math.cos(x * 0.0075) * 5;
}

export function getHorizonY(x) {
  return 182 + Math.sin(x * 0.0036 + 0.3) * 12
             + Math.sin(x * 0.0095 + 1.4) * 6
             + Math.cos(x * 0.022 + 0.5) * 3;
}

let _mushroomAuraCanvas = null;
function getMushroomAuraCanvas() {
  if (!_mushroomAuraCanvas) {
    if (typeof document === 'undefined') return null;
    _mushroomAuraCanvas = document.createElement('canvas');
    _mushroomAuraCanvas.width = 32;
    _mushroomAuraCanvas.height = 32;
    const mctx = _mushroomAuraCanvas.getContext('2d');
    const g = mctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    g.addColorStop(0, 'rgba(103, 232, 249, 0.65)');
    g.addColorStop(0.5, 'rgba(52, 211, 153, 0.25)');
    g.addColorStop(1, 'rgba(0, 0, 0, 0)');
    mctx.fillStyle = g;
    mctx.fillRect(0, 0, 32, 32);
  }
  return _mushroomAuraCanvas;
}

let _gischtCloudCanvas = null;
function getGischtCloudCanvas() {
  if (!_gischtCloudCanvas) {
    if (typeof document === 'undefined') return null;
    _gischtCloudCanvas = document.createElement('canvas');
    _gischtCloudCanvas.width = 64;
    _gischtCloudCanvas.height = 64;
    const gctx = _gischtCloudCanvas.getContext('2d');
    const g = gctx.createRadialGradient(32, 32, 4, 32, 32, 32);
    g.addColorStop(0, 'rgba(240, 253, 250, 1.0)');
    g.addColorStop(0.35, 'rgba(165, 243, 252, 0.65)');
    g.addColorStop(0.7, 'rgba(52, 211, 153, 0.25)');
    g.addColorStop(1, 'rgba(0, 0, 0, 0)');
    gctx.fillStyle = g;
    gctx.fillRect(0, 0, 64, 64);
  }
  return _gischtCloudCanvas;
}

let _cachedFallingLeafGrad = null;
let _cachedHeroStemGrad = null;
let _cachedHeroCapGrad = null;
let _cachedBulrushAuraGrad = null;

// --- STATIC ASSETS & HAND-PLACED CONFIGURATIONS ---

export const STARFIELD = Object.freeze([
  { x: 120, y: 35, r: 1.2, alpha: 0.7, pulseSpeed: 1.1, phase: 0.2 },
  { x: 260, y: 55, r: 1.6, alpha: 0.85, pulseSpeed: 0.9, phase: 1.4 },
  { x: 380, y: 25, r: 1.0, alpha: 0.6, pulseSpeed: 1.4, phase: 2.1 },
  { x: 490, y: 68, r: 1.4, alpha: 0.75, pulseSpeed: 1.2, phase: 0.8 },
  { x: 620, y: 30, r: 1.8, alpha: 0.9, pulseSpeed: 0.8, phase: 3.2 },
  { x: 710, y: 75, r: 1.1, alpha: 0.65, pulseSpeed: 1.3, phase: 1.9 },
  { x: 800, y: 38, r: 1.5, alpha: 0.8, pulseSpeed: 1.0, phase: 0.5 },
  { x: 890, y: 62, r: 1.2, alpha: 0.7, pulseSpeed: 1.5, phase: 2.7 },
  { x: 1040, y: 42, r: 1.3, alpha: 0.75, pulseSpeed: 1.1, phase: 1.1 },
  { x: 1180, y: 28, r: 1.7, alpha: 0.85, pulseSpeed: 0.95, phase: 2.4 },
  { x: 1290, y: 70, r: 1.1, alpha: 0.6, pulseSpeed: 1.3, phase: 3.5 },
  { x: 1410, y: 45, r: 1.5, alpha: 0.8, pulseSpeed: 1.0, phase: 0.9 },
  { x: 1520, y: 60, r: 1.3, alpha: 0.7, pulseSpeed: 1.2, phase: 1.7 },
  { x: 340, y: 95, r: 0.9, alpha: 0.5, pulseSpeed: 1.6, phase: 0.4 },
  { x: 580, y: 110, r: 1.2, alpha: 0.65, pulseSpeed: 1.1, phase: 2.9 },
  { x: 740, y: 105, r: 1.0, alpha: 0.55, pulseSpeed: 1.4, phase: 1.3 },
  { x: 1110, y: 90, r: 1.1, alpha: 0.6, pulseSpeed: 1.2, phase: 3.0 },
  { x: 1350, y: 100, r: 1.4, alpha: 0.75, pulseSpeed: 0.85, phase: 0.7 }
]);

export const DISTANT_MUSHROOM_TREES = Object.freeze([
  { x: 315, h: 165, trunkW: 24, capW: 180, capH: 50, col: 'rgba(20, 42, 36, 0.85)', capCol: 'rgba(24, 52, 44, 0.90)', layer: 2 },
  { x: 390, h: 120, trunkW: 18, capW: 125, capH: 34, col: 'rgba(24, 48, 42, 0.70)', capCol: 'rgba(28, 58, 50, 0.75)', layer: 1 },
  { x: 610, h: 195, trunkW: 20, capW: 155, capH: 42, col: 'rgba(20, 40, 36, 0.52)', capCol: 'rgba(24, 48, 42, 0.58)', layer: 0 },
  { x: 1160, h: 160, trunkW: 24, capW: 175, capH: 48, col: 'rgba(22, 46, 40, 0.82)', capCol: 'rgba(26, 56, 48, 0.88)', layer: 2 },
  { x: 1245, h: 110, trunkW: 16, capW: 115, capH: 30, col: 'rgba(24, 48, 42, 0.65)', capCol: 'rgba(28, 58, 50, 0.70)', layer: 1 }
]);

export const HORIZON_FLORA = Object.freeze([
  { x: 205, type: 'fern', scale: 1.1, hue: '#2e5a3c' },
  { x: 235, type: 'mushroom_cluster', scale: 0.9, glow: '#4ade80' },
  { x: 265, type: 'reeds', scale: 1.1, hue: '#264d34' },
  { x: 395, type: 'reeds', scale: 1.25, hue: '#244730' },
  { x: 425, type: 'alien_bulb', scale: 1.0, glow: '#a3e635' },
  { x: 460, type: 'fern', scale: 1.15, hue: '#326342' },
  { x: 575, type: 'mushroom_cluster', scale: 1.05, glow: '#86efac' },
  { x: 610, type: 'alien_bulb', scale: 0.85, glow: '#f472b6' },
  { x: 775, type: 'reeds', scale: 0.95, hue: '#285038' },
  { x: 840, type: 'fern', scale: 1.1, hue: '#356947' },
  { x: 975, type: 'reeds', scale: 1.15, hue: '#254b32' },
  { x: 1010, type: 'alien_bulb', scale: 1.05, glow: '#a3e635' },
  { x: 1180, type: 'fern', scale: 1.05, hue: '#2e5a3c' },
  { x: 1215, type: 'mushroom_cluster', scale: 1.1, glow: '#4ade80' },
  { x: 1250, type: 'reeds', scale: 1.2, hue: '#264d34' },
  { x: 1375, type: 'fern', scale: 1.1, hue: '#326342' },
  { x: 1405, type: 'alien_bulb', scale: 0.9, glow: '#f472b6' }
]);

export const PUDDLES = Object.freeze([
  {
    id: 1,
    cx: 480,
    cy: 345,
    rx: 55,
    ry: 15,
    nodes: [
      { x: -52, y: -2 }, { x: -34, y: -10 }, { x: -10, y: -14 },
      { x: 18, y: -12 }, { x: 44, y: -7 }, { x: 55, y: 2 },
      { x: 45, y: 10 }, { x: 20, y: 14 }, { x: -8, y: 15 },
      { x: -30, y: 11 }, { x: -48, y: 5 }
    ],
    depthGrade: 0.85,
    satellites: [
      { dx: -58, dy: 10, rx: 14, ry: 5 },
      { dx: 48, dy: -12, rx: 12, ry: 4 }
    ],
    lilyPads: [
      { dx: -18, dy: 2, r: 5, rot: 0.4 },
      { dx: 12, dy: -4, r: 6, rot: 1.8 },
      { dx: 28, dy: 4, r: 4, rot: 3.1 }
    ],
    shoreReeds: [
      { dx: -45, dy: -8, count: 3, h: 18 },
      { dx: 38, dy: 8, count: 4, h: 22 }
    ]
  },
  {
    id: 2,
    cx: 960,
    cy: 375,
    rx: 68,
    ry: 18,
    nodes: [
      { x: -64, y: -1 }, { x: -42, y: -12 }, { x: -14, y: -16 },
      { x: 18, y: -17 }, { x: 50, y: -11 }, { x: 68, y: 2 },
      { x: 56, y: 13 }, { x: 28, y: 17 }, { x: -6, y: 18 },
      { x: -35, y: 14 }, { x: -56, y: 8 }
    ],
    depthGrade: 1.0,
    satellites: [
      { dx: -70, dy: -8, rx: 16, ry: 6 },
      { dx: 62, dy: 12, rx: 15, ry: 5 },
      { dx: 15, dy: 22, rx: 11, ry: 4 }
    ],
    lilyPads: [
      { dx: -24, dy: -4, r: 7, rot: 0.9 },
      { dx: -8, dy: 5, r: 5, rot: 2.3 },
      { dx: 22, dy: -5, r: 6, rot: 4.1 },
      { dx: 42, dy: 2, r: 5, rot: 1.2 }
    ],
    shoreReeds: [
      { dx: -55, dy: 10, count: 4, h: 24 },
      { dx: 52, dy: -10, count: 5, h: 26 },
      { dx: -15, dy: -18, count: 3, h: 16 }
    ],
    sunkenLog: { x1: -35, y1: 10, x2: 25, y2: 16, thick: 4 }
  },
  {
    id: 3,
    cx: 730,
    cy: 435,
    rx: 45,
    ry: 12,
    nodes: [
      { x: -42, y: -1 }, { x: -28, y: -8 }, { x: -6, y: -11 },
      { x: 16, y: -9 }, { x: 38, y: -6 }, { x: 45, y: 2 },
      { x: 34, y: 8 }, { x: 14, y: 11 }, { x: -10, y: 10 },
      { x: -29, y: 7 }
    ],
    depthGrade: 0.75,
    satellites: [
      { dx: -45, dy: 8, rx: 11, ry: 4 },
      { dx: 38, dy: -9, rx: 13, ry: 4.5 }
    ],
    lilyPads: [
      { dx: -12, dy: 1, r: 5, rot: 1.5 },
      { dx: 14, dy: -2, r: 5.5, rot: 3.6 }
    ],
    shoreReeds: [
      { dx: -36, dy: -7, count: 3, h: 18 },
      { dx: 32, dy: 7, count: 3, h: 20 }
    ]
  }
]);

export const HANGING_VINES = Object.freeze([
  { startX: 95, startY: 40, length: 75, maxThick: 3.4, swayPeriod: 3.2, swayAmp: 8, phase: 0.0, drip: true },
  { startX: WIDTH - 430, startY: 70, length: 105, maxThick: 3.2, swayPeriod: 3.1, swayAmp: 10, phase: 1.2, drip: true },
  { startX: WIDTH - 260, startY: 60, length: 125, maxThick: 4.0, swayPeriod: 3.5, swayAmp: 12, phase: 2.9, drip: true },
  { startX: WIDTH - 80, startY: 45, length: 70, maxThick: 3.2, swayPeriod: 3.0, swayAmp: 8, phase: 0.5, drip: true }
]);

export const FOREGROUND_GRASS = Object.freeze([
  { x: 410, y: 520, bladeCount: 6, maxH: 22, amp: 4.5, rate: 2.3, phase: 0.8, col: '#244d36', tipCol: '#52b788' },
  { x: 860, y: 526, bladeCount: 5, maxH: 24, amp: 5.0, rate: 2.1, phase: 2.1, col: '#1b3e2b', tipCol: '#40916c' },
  { x: 1220, y: 522, bladeCount: 6, maxH: 20, amp: 4.0, rate: 2.5, phase: 1.4, col: '#2d5438', tipCol: '#74c69d' }
]);

export const SPORES = Object.freeze([
  { x: 180, y: 260, r: 2.5, speedY: -12, driftAmp: 24, driftFreq: 0.8, phase: 0.5, hue: '#bef264' },
  { x: 310, y: 340, r: 3.2, speedY: -15, driftAmp: 30, driftFreq: 0.7, phase: 1.8, hue: '#a3e635' },
  { x: 440, y: 220, r: 2.0, speedY: -10, driftAmp: 20, driftFreq: 0.9, phase: 3.1, hue: '#fef08a' },
  { x: 570, y: 390, r: 3.5, speedY: -14, driftAmp: 28, driftFreq: 0.6, phase: 0.9, hue: '#bef264' },
  { x: 720, y: 280, r: 2.4, speedY: -11, driftAmp: 22, driftFreq: 0.85, phase: 2.4, hue: '#a3e635' },
  { x: 860, y: 360, r: 3.0, speedY: -16, driftAmp: 32, driftFreq: 0.75, phase: 1.2, hue: '#fef08a' },
  { x: 990, y: 240, r: 2.2, speedY: -13, driftAmp: 25, driftFreq: 0.9, phase: 3.7, hue: '#bef264' },
  { x: 1130, y: 380, r: 3.4, speedY: -15, driftAmp: 29, driftFreq: 0.65, phase: 0.3, hue: '#a3e635' },
  { x: 1270, y: 270, r: 2.6, speedY: -12, driftAmp: 23, driftFreq: 0.8, phase: 2.0, hue: '#fef08a' },
  { x: 1410, y: 330, r: 3.1, speedY: -14, driftAmp: 27, driftFreq: 0.7, phase: 1.5, hue: '#bef264' },
  { x: 220, y: 460, r: 2.8, speedY: -13, driftAmp: 26, driftFreq: 0.8, phase: 2.8, hue: '#bef264' },
  { x: 820, y: 470, r: 3.6, speedY: -16, driftAmp: 34, driftFreq: 0.6, phase: 0.6, hue: '#fef08a' },
  { x: 1360, y: 480, r: 2.9, speedY: -12, driftAmp: 25, driftFreq: 0.75, phase: 3.3, hue: '#a3e635' }
]);

export const DEVIL_SMOKE_PALETTE = [
  { r: 100, g: 116, b: 139 }, // Slate
  { r: 71,  g: 85,  b: 105 },
  { r: 51,  g: 65,  b: 85 },  // Charcoal
  { r: 30,  g: 41,  b: 59 },
  { r: 15,  g: 23,  b: 42 },  // Soot
  { r: 37,  g: 62,  b: 52 },  // Peat
  { r: 28,  g: 52,  b: 43 }
];

export const DEVIL_SMOKE_PARTICLES = (() => {
  const list = [];
  const COUNT = 320;
  const PANORAMA_W = 1800;
  for (let i = 0; i < COUNT; i++) {
    const seed = i * 47.193 + 12.37;
    const s1 = Math.sin(seed);
    const s2 = Math.cos(seed * 1.7);
    const s3 = Math.sin(seed * 3.1);
    const s4 = Math.cos(seed * 5.3);

    const baseX = ((i / COUNT) * PANORAMA_W + s1 * 70) % PANORAMA_W;
    const baseY = 192 + Math.abs(s2) * 68;
    const life = 2.4 + Math.abs(s3) * 2.6;
    const phase = Math.abs(s4) * life;
    const speedX = 22 + Math.abs(s1) * 20;
    const riseDist = 16 + Math.abs(s2) * 28;
    const r0 = 2.8 + Math.abs(s3) * 2.4;
    const rMax = 9.5 + Math.abs(s4) * 6.5;
    const maxA = 0.16 + Math.abs(s1) * 0.14;
    const colorIdx = Math.floor(Math.abs(s2 * 100)) % DEVIL_SMOKE_PALETTE.length;
    const col = DEVIL_SMOKE_PALETTE[colorIdx];
    const wobbleSpeed = 1.6 + Math.abs(s3) * 1.8;
    const wobbleAmp = 2.4 + Math.abs(s4) * 3.6;

    list.push({
      baseX, baseY, life, phase, speedX, riseDist,
      r0, rMax, maxA, col, wobbleSpeed, wobbleAmp
    });
  }
  return Object.freeze(list);
})();

export const DEVIL_SMOKE_EMBERS = (() => {
  const embers = [];
  const EMBER_COUNT = 72;
  const EMBER_COLORS = ['#fbbf24', '#f97316', '#fdba74', '#ef4444', '#fef08a'];
  for (let i = 0; i < EMBER_COUNT; i++) {
    const seed = i * 61.27 + 8.19;
    const s1 = Math.sin(seed);
    const s2 = Math.cos(seed * 2.3);
    const s3 = Math.sin(seed * 4.7);

    const baseX = ((i / EMBER_COUNT) * 1800 + s1 * 80) % 1800;
    const baseY = 202 + Math.abs(s2) * 52;
    const life = 1.1 + Math.abs(s3) * 1.5;
    const phase = Math.abs(s1) * life;
    const speedX = 28 + Math.abs(s2) * 24;
    const riseDist = 20 + Math.abs(s3) * 32;
    const color = EMBER_COLORS[i % EMBER_COLORS.length];
    const size = 1.0 + (i % 3 === 0 ? 0.6 : 0.2);

    embers.push({ baseX, baseY, life, phase, speedX, riseDist, color, size });
  }
  return Object.freeze(embers);
})();

function interpolateColorRgb(c1, c2, factor) {
  const f = Math.max(0, Math.min(1, factor));
  const r = Math.round(c1[0] + (c2[0] - c1[0]) * f);
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * f);
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * f);
  return `rgb(${r},${g},${b})`;
}

const GRASS_TIP_GREEN   = [74, 222, 128]; // #4ade80 (Bioluminescent forest green)
const GRASS_TIP_MINT    = [52, 211, 153]; // #34d399 (Soft mint green)
const GRASS_TIP_CYAN    = [34, 211, 238]; // #22d3ee (Vibrant cyan/teal)
const GRASS_TIP_BLUE    = [56, 189, 248]; // #38bdf8 (Glowing sky blue)
const GRASS_TIP_MINT_FG = [110, 231, 183]; // #6ee7b7 (Foreground mint/cyan)

const GRASS_BASE_GREEN = [28, 62, 38]; // #1c3e26
const GRASS_BASE_BLUE  = [24, 56, 34]; // #183822
const GRASS_BASE_FG    = [19, 43, 27]; // #132b1b

function calcTuftColors(scale, isShoreline, seed) {
  if (isShoreline) {
    return {
      tipCol: '#34d399',
      baseCol: '#1a4329'
    };
  }

  // Subtle organic dither jitter to completely dissolve any row-by-row boundary
  const s = scale + Math.sin(seed * 3.7) * 0.024;

  let tipCol;
  if (s < 0.28) {
    tipCol = `rgb(${GRASS_TIP_GREEN[0]},${GRASS_TIP_GREEN[1]},${GRASS_TIP_GREEN[2]})`;
  } else if (s < 0.35) {
    tipCol = interpolateColorRgb(GRASS_TIP_GREEN, GRASS_TIP_MINT, (s - 0.28) / (0.35 - 0.28));
  } else if (s < 0.41) {
    // Mint -> Cyan transition (the requested smooth cyan bridge!)
    tipCol = interpolateColorRgb(GRASS_TIP_MINT, GRASS_TIP_CYAN, (s - 0.35) / (0.41 - 0.35));
  } else if (s < 0.48) {
    // Cyan -> Blue transition
    tipCol = interpolateColorRgb(GRASS_TIP_CYAN, GRASS_TIP_BLUE, (s - 0.41) / (0.48 - 0.41));
  } else if (s < 0.70) {
    // Midground glowing blue
    tipCol = `rgb(${GRASS_TIP_BLUE[0]},${GRASS_TIP_BLUE[1]},${GRASS_TIP_BLUE[2]})`;
  } else {
    // Foreground Blue -> Mint transition
    tipCol = interpolateColorRgb(GRASS_TIP_BLUE, GRASS_TIP_MINT_FG, (s - 0.70) / (1.0 - 0.70));
  }

  let baseCol;
  if (s < 0.40) {
    baseCol = interpolateColorRgb(GRASS_BASE_GREEN, GRASS_BASE_BLUE, (s - 0.22) / (0.40 - 0.22));
  } else {
    baseCol = interpolateColorRgb(GRASS_BASE_BLUE, GRASS_BASE_FG, (s - 0.40) / (1.0 - 0.40));
  }

  return { tipCol, baseCol };
}

export const PERSPECTIVE_GRASS_TUFTS = (() => {
  const tufts = [];
  const NUM_ROWS = 36;
  const Y_START = 172;
  const Y_END = 535;

  const PUDDLE_BOUNDS = [
    { cx: 480, cy: 345, rx: 55, ry: 15 },
    { cx: 960, cy: 375, rx: 68, ry: 18 },
    { cx: 740, cy: 435, rx: 42, ry: 12 }
  ];

  for (let r = 0; r < NUM_ROWS; r++) {
    const depthFrac = Math.pow(r / (NUM_ROWS - 1), 1.05);
    const rowY = Y_START + depthFrac * (Y_END - Y_START);
    const scale = 0.22 + 0.78 * Math.pow(depthFrac, 1.15);
    const stepX = 12 + depthFrac * 22;

    for (let x = -10; x <= 1610; x += stepX) {
      const seed = r * 137.5 + x * 0.41;
      const jx = Math.sin(seed) * (stepX * 0.42);
      const jy = Math.cos(seed * 1.3) * (2.8 * scale);
      const tuftX = x + jx;
      const tuftY = rowY + jy;

      if (tuftY < getHorizonY(tuftX) + 1) continue;

      let inPuddle = false;
      let isShoreline = false;
      for (const p of PUDDLE_BOUNDS) {
        const dx = (tuftX - p.cx) / p.rx;
        const dy = (tuftY - p.cy) / p.ry;
        const distSq = dx * dx + dy * dy;
        if (distSq < 0.72) {
          inPuddle = true;
          break;
        } else if (distSq < 1.18) {
          isShoreline = true;
        }
      }
      if (inPuddle) continue;

      const baseH = (4.5 + scale * 12.5) * (0.85 + Math.sin(seed * 2.1) * 0.25);
      const bladeCount = scale > 0.75 ? 3 : 2;
      const strokeW = Math.max(1.0, scale * 1.65);
      const colors = calcTuftColors(scale, isShoreline, seed);

      tufts.push({
        x: tuftX,
        y: tuftY,
        scale,
        h: baseH,
        count: bladeCount,
        w: strokeW,
        isShoreline,
        tipCol: colors.tipCol,
        baseCol: colors.baseCol,
        phase: (seed % 6.28)
      });
    }
  }
  return Object.freeze(tufts);
})();

// Fast-path Render Batching: pre-grouped grass tufts by quantized (baseColor, tipColor, strokeWidth)
// Reduces per-frame Canvas 2D stroke() calls from ~5,700 to ~44 calls while preserving 100% density and wind sway.
function quantizeColorRgb(rgbStr, levels = 12) {
  const match = rgbStr.match(/\d+/g);
  if (!match) return rgbStr;
  const step = 256 / levels;
  const r = Math.min(255, Math.round(parseInt(match[0]) / step) * Math.round(step));
  const g = Math.min(255, Math.round(parseInt(match[1]) / step) * Math.round(step));
  const b = Math.min(255, Math.round(parseInt(match[2]) / step) * Math.round(step));
  return `rgb(${r},${g},${b})`;
}

export const PERSPECTIVE_GRASS_BUCKETS = (() => {
  const map = new Map();
  for (let i = 0; i < PERSPECTIVE_GRASS_TUFTS.length; i++) {
    const t = PERSPECTIVE_GRASS_TUFTS[i];
    const bCol = quantizeColorRgb(t.baseCol, 12);
    const tCol = quantizeColorRgb(t.tipCol, 12);
    const w = Math.round(t.w * 2) / 2;
    const key = `${bCol}|${tCol}|${w}`;
    if (!map.has(key)) {
      map.set(key, { baseCol: bCol, tipCol: tCol, w, tufts: [] });
    }
    map.get(key).tufts.push(t);
  }
  const buckets = Array.from(map.values());
  // Sort buckets by average tuft Y ascending (horizon to foreground)
  // to ensure correct back-to-front perspective depth and prevent inverted overlays
  for (let k = 0; k < buckets.length; k++) {
    const b = buckets[k];
    b.avgY = b.tufts.reduce((sum, tuft) => sum + tuft.y, 0) / b.tufts.length;
  }
  buckets.sort((a, b) => a.avgY - b.avgY);
  return Object.freeze(buckets);
})();

// --- STATIC DRAWING HELPERS ---

function drawSkyStatic(ctx, bounds) {
  const { visibleTop, visibleLeft, visibleRight } = bounds;
  const bgWidth = visibleRight - visibleLeft;

  const skyGrad = ctx.createLinearGradient(0, visibleTop, 0, SKY_BOTTOM);
  skyGrad.addColorStop(0, '#010507');
  skyGrad.addColorStop(0.35, '#051419');
  skyGrad.addColorStop(0.7, '#0c2727');
  skyGrad.addColorStop(1, '#184236');
  ctx.fillStyle = skyGrad;
  ctx.fillRect(visibleLeft, visibleTop, bgWidth, SKY_BOTTOM - visibleTop);

  // Western Rose/Magenta Nebula Cloud
  const wNebula = ctx.createRadialGradient(360, 60, 20, 360, 60, 380);
  wNebula.addColorStop(0, 'rgba(217, 70, 239, 0.16)');
  wNebula.addColorStop(0.35, 'rgba(192, 38, 211, 0.10)');
  wNebula.addColorStop(0.65, 'rgba(126, 34, 206, 0.04)');
  wNebula.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = wNebula;
  ctx.fillRect(visibleLeft, visibleTop, Math.max(750, 750 - visibleLeft), SKY_BOTTOM - visibleTop);

  // Eastern Cyan/Emerald Supernova Haze
  const eNebula = ctx.createRadialGradient(1250, 70, 20, 1250, 70, 360);
  eNebula.addColorStop(0, 'rgba(20, 184, 166, 0.14)');
  eNebula.addColorStop(0.45, 'rgba(13, 148, 136, 0.08)');
  eNebula.addColorStop(0.8, 'rgba(15, 118, 110, 0.02)');
  eNebula.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = eNebula;
  ctx.fillRect(800, visibleTop, visibleRight - 800, SKY_BOTTOM - visibleTop);

  // Static Starfield (always visible on low, shimmer/pulse added dynamically on higher tiers)
  for (const s of STARFIELD) {
    ctx.fillStyle = `rgba(230, 245, 240, ${s.alpha * 0.75})`;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawMoonsStatic(ctx) {
  const mx = 960;
  const my = 90;
  const mr = 56;

  // Static Moon Halos (ensures ethereal lunar glow is present on Low settings)
  const halo = ctx.createRadialGradient(mx, my, mr * 0.6, mx, my, mr * 3.5);
  halo.addColorStop(0, 'rgba(215, 245, 235, 0.22)');
  halo.addColorStop(0.35, 'rgba(167, 243, 208, 0.09)');
  halo.addColorStop(0.7, 'rgba(52, 211, 153, 0.03)');
  halo.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(mx, my, mr * 3.5, 0, Math.PI * 2);
  ctx.fill();

  // Moon sphere with 3D limb shading
  const moonGrad = ctx.createRadialGradient(mx - mr * 0.3, my - mr * 0.3, mr * 0.1, mx, my, mr);
  moonGrad.addColorStop(0, '#f2fbf7');
  moonGrad.addColorStop(0.55, '#d4ece3');
  moonGrad.addColorStop(0.88, '#9fc7ba');
  moonGrad.addColorStop(1, '#658d81');
  ctx.fillStyle = moonGrad;
  ctx.beginPath();
  ctx.arc(mx, my, mr, 0, Math.PI * 2);
  ctx.fill();

  // Lunar crater details & surface mares
  ctx.save();
  ctx.beginPath();
  ctx.arc(mx, my, mr, 0, Math.PI * 2);
  ctx.clip();

  ctx.fillStyle = 'rgba(78, 115, 104, 0.28)';
  ctx.beginPath();
  ctx.ellipse(mx - 14, my - 12, 18, 12, 0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(mx + 18, my + 14, 22, 14, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(mx - 8, my + 24, 14, 9, 0.2, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(mx + 12, my - 22, 7, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = 'rgba(60, 92, 82, 0.35)';
  ctx.fill();

  const termGrad = ctx.createLinearGradient(mx - mr, my, mx + mr, my);
  termGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
  termGrad.addColorStop(0.65, 'rgba(0, 0, 0, 0)');
  termGrad.addColorStop(1, 'rgba(12, 28, 24, 0.48)');
  ctx.fillStyle = termGrad;
  ctx.fillRect(mx - mr, my - mr, mr * 2, mr * 2);
  ctx.restore();

  // Companion Moon
  const sx = 840;
  const sy = 52;
  const sr = 28;

  const sHalo = ctx.createRadialGradient(sx, sy, sr * 0.5, sx, sy, sr * 2.8);
  sHalo.addColorStop(0, 'rgba(254, 243, 199, 0.18)');
  sHalo.addColorStop(0.5, 'rgba(251, 191, 36, 0.05)');
  sHalo.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = sHalo;
  ctx.beginPath();
  ctx.arc(sx, sy, sr * 2.8, 0, Math.PI * 2);
  ctx.fill();

  const sGrad = ctx.createRadialGradient(sx - sr * 0.3, sy - sr * 0.3, sr * 0.1, sx, sy, sr);
  sGrad.addColorStop(0, '#fef9c3');
  sGrad.addColorStop(0.6, '#fde68a');
  sGrad.addColorStop(0.9, '#ca8a04');
  sGrad.addColorStop(1, '#854d0e');
  ctx.fillStyle = sGrad;
  ctx.beginPath();
  ctx.arc(sx, sy, sr, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(sx, sy, sr, 0, Math.PI * 2);
  ctx.clip();
  const sTerm = ctx.createLinearGradient(sx - sr, sy, sx + sr, sy);
  sTerm.addColorStop(0, 'rgba(0, 0, 0, 0)');
  sTerm.addColorStop(0.6, 'rgba(0, 0, 0, 0)');
  sTerm.addColorStop(1, 'rgba(20, 15, 10, 0.45)');
  ctx.fillStyle = sTerm;
  ctx.fillRect(sx - sr, sy - sr, sr * 2, sr * 2);
  ctx.restore();
}

function drawDistantMushroomForest(ctx, bounds) {
  const { visibleLeft, visibleRight } = bounds;

  ctx.fillStyle = 'rgba(14, 38, 32, 0.55)';
  ctx.beginPath();
  ctx.moveTo(visibleLeft, getFarRidgeY(visibleLeft));
  for (let x = visibleLeft + 15; x <= visibleRight; x += 15) {
    ctx.lineTo(x, getFarRidgeY(x));
  }
  ctx.lineTo(visibleRight, 230);
  ctx.lineTo(visibleLeft, 230);
  ctx.closePath();
  ctx.fill();

  for (const tree of DISTANT_MUSHROOM_TREES) {
    const baseY = tree.layer === 0 ? getFarRidgeY(tree.x) : getHorizonY(tree.x);
    const topY = baseY - tree.h;

    ctx.fillStyle = tree.col;
    ctx.beginPath();
    ctx.moveTo(tree.x - tree.trunkW * 0.35, topY + tree.capH * 0.4);
    ctx.lineTo(tree.x + tree.trunkW * 0.35, topY + tree.capH * 0.4);
    ctx.quadraticCurveTo(tree.x + tree.trunkW * 0.3, baseY - tree.h * 0.4, tree.x + tree.trunkW * 0.6, baseY);
    ctx.lineTo(tree.x - tree.trunkW * 0.6, baseY);
    ctx.quadraticCurveTo(tree.x - tree.trunkW * 0.3, baseY - tree.h * 0.4, tree.x - tree.trunkW * 0.35, topY + tree.capH * 0.4);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = tree.capCol;
    ctx.beginPath();
    ctx.ellipse(tree.x, topY, tree.capW * 0.5, tree.capH * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(10, 24, 20, 0.45)';
    ctx.beginPath();
    ctx.ellipse(tree.x, topY + tree.capH * 0.25, tree.capW * 0.45, tree.capH * 0.25, 0, 0, Math.PI);
    ctx.fill();

    ctx.strokeStyle = 'rgba(94, 180, 150, 0.3)';
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.ellipse(tree.x, topY, tree.capW * 0.49, tree.capH * 0.48, 0, Math.PI * 0.8, Math.PI * 2.2);
    ctx.stroke();

    const tendrilCount = 4;
    for (let i = 0; i < tendrilCount; i++) {
      const tx = tree.x - tree.capW * 0.3 + (tree.capW * 0.6 / (tendrilCount - 1)) * i;
      const tLen = 14 + ((i * 13) % 16);
      ctx.strokeStyle = tree.capCol;
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.moveTo(tx, topY + tree.capH * 0.3);
      ctx.lineTo(tx, topY + tree.capH * 0.3 + tLen);
      ctx.stroke();
    }
  }

  ctx.save();
  const mistGrad = ctx.createLinearGradient(0, 150, 0, 215);
  mistGrad.addColorStop(0, 'rgba(26, 68, 54, 0)');
  mistGrad.addColorStop(0.65, 'rgba(38, 92, 74, 0.32)');
  mistGrad.addColorStop(1, 'rgba(24, 52, 42, 0.60)');
  ctx.fillStyle = mistGrad;
  ctx.beginPath();
  ctx.moveTo(visibleLeft, getFarRidgeY(visibleLeft) - 10);
  for (let x = visibleLeft + 15; x <= visibleRight; x += 15) {
    ctx.lineTo(x, getFarRidgeY(x) - 10);
  }
  ctx.lineTo(visibleRight, 230);
  ctx.lineTo(visibleLeft, 230);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawWaterfallStatic(ctx) {
  const hillPeakX = 244;
  const hillPeakY = 66;
  const fallBaseY = getHorizonY(hillPeakX) + 2;

  const wx = hillPeakX + 3;
  const topY = hillPeakY;
  const ledge1Y = hillPeakY + 40;
  const ledge2Y = hillPeakY + 76;
  const botY = fallBaseY;

  ctx.save();

  // Top source pool glow
  const srcGlow = ctx.createRadialGradient(wx, topY, 4, wx, topY, 36);
  srcGlow.addColorStop(0, 'rgba(103, 232, 249, 0.92)');
  srcGlow.addColorStop(0.45, 'rgba(52, 211, 153, 0.48)');
  srcGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = srcGlow;
  ctx.beginPath();
  ctx.arc(wx, topY, 36, 0, Math.PI * 2);
  ctx.fill();

  // Weir lip
  const weirW = 20;
  const lipGrad = ctx.createLinearGradient(wx - weirW * 0.5, topY - 3, wx + weirW * 0.5, topY + 4);
  lipGrad.addColorStop(0, 'rgba(56, 189, 248, 0.85)');
  lipGrad.addColorStop(0.25, 'rgba(165, 243, 252, 0.98)');
  lipGrad.addColorStop(0.5, '#ffffff');
  lipGrad.addColorStop(0.75, 'rgba(165, 243, 252, 0.98)');
  lipGrad.addColorStop(1, 'rgba(56, 189, 248, 0.85)');
  ctx.fillStyle = lipGrad;
  ctx.beginPath();
  ctx.ellipse(wx, topY, weirW * 0.5, 3.8, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.ellipse(wx, topY - 0.9, weirW * 0.38, 1.8, 0, Math.PI * 0.8, Math.PI * 2.2);
  ctx.stroke();

  // Tier 1
  const t1TopW = 19;
  const t1BotW = 28;
  const t1Grad = ctx.createLinearGradient(wx, topY, wx, ledge1Y);
  t1Grad.addColorStop(0, 'rgba(186, 230, 253, 0.96)');
  t1Grad.addColorStop(0.35, 'rgba(56, 189, 248, 0.92)');
  t1Grad.addColorStop(0.8, 'rgba(14, 165, 233, 0.94)');
  t1Grad.addColorStop(1, 'rgba(2, 132, 199, 0.96)');
  ctx.fillStyle = t1Grad;
  ctx.beginPath();
  ctx.moveTo(wx - t1TopW * 0.5, topY);
  ctx.lineTo(wx - t1BotW * 0.5, ledge1Y);
  ctx.lineTo(wx + t1BotW * 0.5, ledge1Y);
  ctx.lineTo(wx + t1TopW * 0.5, topY);
  ctx.closePath();
  ctx.fill();

  // Tier 2
  const t2TopW = 27;
  const t2BotW = 39;
  const t2Grad = ctx.createLinearGradient(wx, ledge1Y, wx, ledge2Y);
  t2Grad.addColorStop(0, 'rgba(207, 250, 254, 0.97)');
  t2Grad.addColorStop(0.4, 'rgba(56, 189, 248, 0.93)');
  t2Grad.addColorStop(1, 'rgba(14, 165, 233, 0.95)');
  ctx.fillStyle = t2Grad;
  ctx.beginPath();
  ctx.moveTo(wx - t2TopW * 0.5, ledge1Y);
  ctx.lineTo(wx - t2BotW * 0.5, ledge2Y);
  ctx.lineTo(wx + t2BotW * 0.5, ledge2Y);
  ctx.lineTo(wx + t2TopW * 0.5, ledge1Y);
  ctx.closePath();
  ctx.fill();

  // Tier 3
  const t3TopW = 38;
  const t3BotW = 54;
  const t3Grad = ctx.createLinearGradient(wx, ledge2Y, wx, botY);
  t3Grad.addColorStop(0, 'rgba(224, 254, 249, 0.98)');
  t3Grad.addColorStop(0.4, 'rgba(34, 211, 238, 0.94)');
  t3Grad.addColorStop(0.85, 'rgba(14, 165, 233, 0.96)');
  t3Grad.addColorStop(1, 'rgba(2, 132, 199, 0.98)');
  ctx.fillStyle = t3Grad;
  ctx.beginPath();
  ctx.moveTo(wx - t3TopW * 0.5, ledge2Y);
  ctx.lineTo(wx - t3BotW * 0.5, botY);
  ctx.lineTo(wx + t3BotW * 0.5, botY);
  ctx.lineTo(wx + t3TopW * 0.5, ledge2Y);
  ctx.closePath();
  ctx.fill();

  // Flumes
  ctx.strokeStyle = 'rgba(165, 243, 252, 0.85)';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(wx - 12, 78);
  ctx.quadraticCurveTo(wx - 22, 108, wx - 16, ledge2Y + 6);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(207, 250, 254, 0.9)';
  ctx.lineWidth = 2.8;
  ctx.beginPath();
  ctx.moveTo(wx + 14, 98);
  ctx.quadraticCurveTo(wx + 26, 136, wx + 20, botY);
  ctx.stroke();

  // Ledge 1 & 2 splash crescents
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.ellipse(wx, ledge1Y, 18, 2.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(wx, ledge2Y, 24, 3.2, 0, 0, Math.PI * 2);
  ctx.fill();

  // Waterfall Base Splash Basin Mist Workaround (compensates for 0 smoke particles on Low)
  const mistGlow = ctx.createRadialGradient(wx, botY, 10, wx, botY, 70);
  mistGlow.addColorStop(0, 'rgba(165, 243, 252, 0.50)');
  mistGlow.addColorStop(0.35, 'rgba(56, 189, 248, 0.25)');
  mistGlow.addColorStop(0.7, 'rgba(14, 165, 233, 0.08)');
  mistGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = mistGlow;
  ctx.beginPath();
  ctx.ellipse(wx, botY + 4, 70, 24, 0, 0, Math.PI * 2);
  ctx.fill();

  // Pool ripples
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.ellipse(wx, botY + 2, 28, 5, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(165, 243, 252, 0.5)';
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.ellipse(wx, botY + 6, 46, 8, 0, 0, Math.PI * 2);
  ctx.stroke();

  ctx.restore();
}

function drawWaterfallBasaltHill(ctx) {
  const hillBaseX = 115;
  const hillPeakX = 244;
  const hillEndX = 385;
  const hillPeakY = 66;
  const fallBaseY = getHorizonY(hillPeakX) + 2;

  const wx = hillPeakX + 3;
  const topY = hillPeakY;
  const ledge1Y = hillPeakY + 40;
  const ledge2Y = hillPeakY + 76;
  const botY = fallBaseY;

  ctx.save();

  ctx.beginPath();
  ctx.moveTo(hillBaseX, getHorizonY(hillBaseX));
  ctx.bezierCurveTo(140, 145, 175, 105, hillPeakX - 22, hillPeakY);
  ctx.lineTo(hillPeakX + 28, hillPeakY);
  ctx.bezierCurveTo(305, 105, 345, 145, hillEndX, getHorizonY(hillEndX));
  for (let bx = hillEndX; bx >= hillBaseX; bx -= 12) {
    ctx.lineTo(bx, getHorizonY(bx) + 6);
  }
  ctx.closePath();

  const hillGrad = ctx.createLinearGradient(hillPeakX, hillPeakY, hillPeakX, 210);
  hillGrad.addColorStop(0, '#1c3829');
  hillGrad.addColorStop(0.35, '#14281e');
  hillGrad.addColorStop(0.7, '#0d1a13');
  hillGrad.addColorStop(1, '#1c2e22');
  ctx.fillStyle = hillGrad;
  ctx.fill();

  const leftSkirtGrad = ctx.createRadialGradient(150, 165, 5, 150, 165, 55);
  leftSkirtGrad.addColorStop(0, 'rgba(28, 56, 38, 0.85)');
  leftSkirtGrad.addColorStop(0.6, 'rgba(20, 40, 28, 0.45)');
  leftSkirtGrad.addColorStop(1, 'rgba(20, 40, 28, 0)');
  ctx.fillStyle = leftSkirtGrad;
  ctx.beginPath();
  ctx.ellipse(150, 168, 55, 26, -0.15, 0, Math.PI * 2);
  ctx.fill();

  const rightSkirtGrad = ctx.createRadialGradient(340, 160, 5, 340, 160, 60);
  rightSkirtGrad.addColorStop(0, 'rgba(28, 56, 38, 0.85)');
  rightSkirtGrad.addColorStop(0.6, 'rgba(20, 40, 28, 0.45)');
  rightSkirtGrad.addColorStop(1, 'rgba(20, 40, 28, 0)');
  ctx.fillStyle = rightSkirtGrad;
  ctx.beginPath();
  ctx.ellipse(340, 162, 60, 28, 0.15, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(7, 16, 11, 0.65)';
  ctx.lineWidth = 2.0;
  for (let fx of [160, 185, 212, 282, 312, 340, 362]) {
    ctx.beginPath();
    ctx.moveTo(fx, 92 + ((fx * 13) % 28));
    ctx.quadraticCurveTo(fx + ((fx % 2 === 0) ? -4 : 5), 140, fx + ((fx % 2 === 0) ? 2 : -3), getHorizonY(fx));
    ctx.stroke();
  }

  ctx.fillStyle = '#102218';
  ctx.beginPath();
  ctx.moveTo(160, 138);
  ctx.quadraticCurveTo(185, 128, 208, 136);
  ctx.quadraticCurveTo(185, 146, 160, 138);
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(285, 132);
  ctx.quadraticCurveTo(312, 126, 340, 138);
  ctx.quadraticCurveTo(312, 146, 285, 132);
  ctx.fill();

  ctx.strokeStyle = '#38734d';
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(hillBaseX, getHorizonY(hillBaseX));
  ctx.bezierCurveTo(140, 145, 175, 105, hillPeakX - 22, hillPeakY);
  ctx.moveTo(hillPeakX + 28, hillPeakY);
  ctx.bezierCurveTo(305, 105, 345, 145, hillEndX, getHorizonY(hillEndX));
  ctx.stroke();

  ctx.strokeStyle = 'rgba(74, 222, 128, 0.45)';
  ctx.lineWidth = 1.3;
  ctx.stroke();

  ctx.fillStyle = '#040d07';
  ctx.beginPath();
  ctx.moveTo(wx - 26, topY);
  ctx.bezierCurveTo(wx - 24, ledge1Y, wx - 34, ledge2Y, wx - 42, botY);
  ctx.lineTo(wx + 42, botY);
  ctx.bezierCurveTo(wx + 34, ledge2Y, wx + 24, ledge1Y, wx + 26, topY);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = 'rgba(6, 78, 59, 0.45)';
  ctx.lineWidth = 2.0;
  for (let ry of [topY + 18, ledge1Y - 4, ledge1Y + 18, ledge2Y - 6, ledge2Y + 20]) {
    ctx.beginPath();
    ctx.moveTo(wx - 28, ry);
    ctx.quadraticCurveTo(wx, ry + 3, wx + 28, ry - 1);
    ctx.stroke();
  }

  ctx.restore();
}

function drawSwampFloorStatic(ctx, bounds, laneBottom, height) {
  const { visibleBottom, visibleLeft, visibleRight } = bounds;

  const floorGrad = ctx.createLinearGradient(0, 160, 0, Math.max(height, visibleBottom));
  floorGrad.addColorStop(0, '#1c2e22');
  floorGrad.addColorStop(0.12, '#15241b');
  floorGrad.addColorStop(0.45, '#101c15');
  floorGrad.addColorStop(0.8, '#0b140f');
  floorGrad.addColorStop(1, '#060a08');

  ctx.beginPath();
  ctx.moveTo(visibleLeft, getHorizonY(visibleLeft));
  for (let x = visibleLeft + 10; x <= visibleRight; x += 10) {
    ctx.lineTo(x, getHorizonY(x));
  }
  ctx.lineTo(visibleRight, visibleBottom);
  ctx.lineTo(visibleLeft, visibleBottom);
  ctx.closePath();
  ctx.fillStyle = floorGrad;
  ctx.fill();

  ctx.save();
  ctx.strokeStyle = 'rgba(28, 50, 36, 0.32)';
  ctx.lineWidth = 1.0;
  for (let sy = 220; sy <= laneBottom; sy += 16) {
    ctx.beginPath();
    const xStart = Math.max(visibleLeft + 10, 40 + ((sy * 23) % 60));
    const xEnd = Math.min(visibleRight - 10, WIDTH - 40 - ((sy * 37) % 60));
    ctx.moveTo(xStart, sy);
    for (let sx = xStart + 80; sx <= xEnd; sx += 80) {
      const dy = Math.sin(sx * 0.015 + sy * 0.08) * 1.8;
      ctx.lineTo(sx, sy + dy);
    }
    ctx.stroke();
  }
  ctx.restore();

  const HUMMOCKS = [
    { x: 260, y: 240, rx: 45, ry: 14, col: 'rgba(36, 62, 44, 0.45)' },
    { x: 580, y: 265, rx: 55, ry: 16, col: 'rgba(28, 48, 34, 0.5)' },
    { x: 890, y: 295, rx: 60, ry: 18, col: 'rgba(32, 54, 38, 0.45)' },
    { x: 1200, y: 250, rx: 50, ry: 15, col: 'rgba(30, 52, 36, 0.4)' },
    { x: 380, y: 465, rx: 70, ry: 20, col: 'rgba(20, 36, 26, 0.55)' },
    { x: 1080, y: 470, rx: 75, ry: 22, col: 'rgba(18, 32, 22, 0.6)' }
  ];
  for (const h of HUMMOCKS) {
    const rad = ctx.createRadialGradient(h.x, h.y - h.ry * 0.3, 2, h.x, h.y, h.rx);
    rad.addColorStop(0, h.col);
    rad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = rad;
    ctx.beginPath();
    ctx.ellipse(h.x, h.y, h.rx, h.ry, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(74, 222, 128, 0.16)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.ellipse(h.x, h.y - h.ry * 0.3, h.rx * 0.7, h.ry * 0.4, 0, Math.PI, Math.PI * 2);
    ctx.stroke();
  }

  ctx.strokeStyle = 'rgba(74, 222, 128, 0.22)';
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  ctx.moveTo(visibleLeft, getHorizonY(visibleLeft));
  for (let x = visibleLeft + 10; x <= visibleRight; x += 10) {
    ctx.lineTo(x, getHorizonY(x));
  }
  ctx.stroke();

  const PEAT_PATCHES = [
    { x: 340, y: 260, rx: 75, ry: 24, col: 'rgba(32, 54, 38, 0.4)' },
    { x: 620, y: 310, rx: 90, ry: 28, col: 'rgba(22, 40, 28, 0.5)' },
    { x: 1080, y: 280, rx: 95, ry: 30, col: 'rgba(30, 52, 36, 0.45)' },
    { x: 440, y: 440, rx: 80, ry: 25, col: 'rgba(16, 28, 20, 0.6)' },
    { x: 1220, y: 440, rx: 85, ry: 26, col: 'rgba(18, 32, 22, 0.55)' },
    { x: 840, y: 480, rx: 110, ry: 32, col: 'rgba(12, 22, 16, 0.7)' }
  ];
  for (const p of PEAT_PATCHES) {
    const rad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.rx);
    rad.addColorStop(0, p.col);
    rad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = rad;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, p.rx, p.ry, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawMudTiles(ctx) {
  const TILE_GROUPS = [
    { cx: 425, cy: 335, scale: 0.9, cols: 5, rows: 3 },
    { cx: 535, cy: 355, scale: 0.85, cols: 4, rows: 2 },
    { cx: 895, cy: 365, scale: 1.0, cols: 5, rows: 3 },
    { cx: 1025, cy: 382, scale: 0.9, cols: 4, rows: 2 },
    { cx: 685, cy: 425, scale: 0.8, cols: 4, rows: 2 },
    { cx: 775, cy: 442, scale: 0.85, cols: 4, rows: 2 }
  ];

  ctx.save();
  for (const g of TILE_GROUPS) {
    for (let r = 0; r < g.rows; r++) {
      for (let c = 0; c < g.cols; c++) {
        const jx = ((c * 17 + r * 31) % 11) - 5;
        const jy = ((c * 23 + r * 13) % 9) - 4;
        const tx = g.cx + (c - g.cols * 0.5) * 16 * g.scale + jx;
        const ty = g.cy + (r - g.rows * 0.5) * 11 * g.scale + jy;
        const tw = 12 * g.scale;
        const th = 7 * g.scale;

        ctx.fillStyle = 'rgba(18, 32, 23, 0.30)';
        ctx.beginPath();
        ctx.moveTo(tx - tw * 0.45, ty - th * 0.5);
        ctx.lineTo(tx + tw * 0.4, ty - th * 0.45);
        ctx.lineTo(tx + tw * 0.5, ty + th * 0.4);
        ctx.lineTo(tx - tw * 0.35, ty + th * 0.5);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = 'rgba(10, 20, 14, 0.25)';
        ctx.lineWidth = 0.8;
        ctx.stroke();

        ctx.strokeStyle = 'rgba(74, 140, 105, 0.10)';
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(tx - tw * 0.45, ty - th * 0.5);
        ctx.lineTo(tx + tw * 0.4, ty - th * 0.45);
        ctx.stroke();
      }
    }
  }
  ctx.restore();
}

function drawMudRuts(ctx) {
  const RUT_PATHS = [
    [
      { x: 310, y: 250 },
      { x: 370, y: 285 },
      { x: 420, y: 315 },
      { x: 480, y: 345 }
    ],
    [
      { x: 490, y: 350 },
      { x: 560, y: 375 },
      { x: 640, y: 405 },
      { x: 730, y: 435 }
    ],
    [
      { x: 740, y: 435 },
      { x: 810, y: 415 },
      { x: 885, y: 395 },
      { x: 960, y: 375 }
    ],
    [
      { x: 1140, y: 270 },
      { x: 1070, y: 310 },
      { x: 1015, y: 345 },
      { x: 960, y: 375 }
    ]
  ];

  ctx.save();
  for (let rIdx = 0; rIdx < RUT_PATHS.length; rIdx++) {
    const rut = RUT_PATHS[rIdx];

    ctx.beginPath();
    ctx.moveTo(rut[0].x, rut[0].y);
    for (let i = 1; i < rut.length; i++) {
      const prev = rut[i - 1];
      const curr = rut[i];
      const mx = (prev.x + curr.x) * 0.5;
      const my = (prev.y + curr.y) * 0.5;
      ctx.quadraticCurveTo(prev.x, prev.y, mx, my);
    }
    const last = rut[rut.length - 1];
    ctx.lineTo(last.x, last.y);
    // Soft multi-tiered feathered wash into the landscape (no hard strokes or sharp edge lines)
    const FEATHER_TIERS = [
      { width: 44, strokeStyle: 'rgba(14, 26, 19, 0.08)' },
      { width: 30, strokeStyle: 'rgba(12, 23, 17, 0.14)' },
      { width: 18, strokeStyle: 'rgba(10, 20, 14, 0.22)' },
      { width: 9,  strokeStyle: 'rgba(8, 16, 12, 0.32)' },
      { width: 4,  strokeStyle: 'rgba(6, 12, 9, 0.40)' }
    ];
    for (const tier of FEATHER_TIERS) {
      ctx.strokeStyle = tier.strokeStyle;
      ctx.lineWidth = tier.width;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawEnrichedGroundDetailsStatic(ctx) {
  ctx.save();

  const BERRY_CLUSTERS = [
    {
      bx: 385, by: 422,
      stems: [{ sx: -16, sy: 6, mx: -6, my: -4, ex: 14, ey: -2 }, { sx: -8, sy: 2, mx: 4, my: 6, ex: 18, ey: 4 }],
      leaves: [{ lx: -12, ly: 2, rot: -0.4 }, { lx: -4, ly: -5, rot: 0.3 }, { lx: 6, ly: -3, rot: -0.2 }, { lx: 14, ly: 2, rot: 0.5 }],
      berries: [{ x: -14, y: 5, r: 2.6, col: '#e11d48' }, { x: -7, y: -4, r: 3.1, col: '#be123c' }, { x: 0, y: -2, r: 2.4, col: '#f43f5e' }, { x: 7, y: -5, r: 3.0, col: '#e11d48' }, { x: 15, y: 3, r: 2.5, col: '#be123c' }]
    },
    {
      bx: 715, by: 372,
      stems: [{ sx: -18, sy: 4, mx: -4, my: -5, ex: 16, ey: 0 }, { sx: -10, sy: 0, mx: 2, my: 5, ex: 14, ey: 6 }],
      leaves: [{ lx: -14, ly: 1, rot: -0.3 }, { lx: -6, ly: -6, rot: 0.25 }, { lx: 4, ly: -2, rot: -0.35 }, { lx: 12, ly: 4, rot: 0.4 }],
      berries: [{ x: -15, y: 3, r: 2.8, col: '#be123c' }, { x: -5, y: -5, r: 3.2, col: '#f43f5e' }, { x: 2, y: -3, r: 2.5, col: '#e11d48' }, { x: 11, y: 1, r: 3.0, col: '#be123c' }, { x: 17, y: 5, r: 2.4, col: '#f43f5e' }]
    },
    {
      bx: 1050, by: 432,
      stems: [{ sx: -14, sy: 5, mx: -2, my: -3, ex: 15, ey: 2 }],
      leaves: [{ lx: -10, ly: 2, rot: -0.4 }, { lx: 2, ly: -4, rot: 0.3 }, { lx: 11, ly: 1, rot: -0.2 }],
      berries: [{ x: -12, y: 4, r: 2.9, col: '#e11d48' }, { x: -3, y: -3, r: 3.3, col: '#f43f5e' }, { x: 5, y: -2, r: 2.6, col: '#be123c' }, { x: 14, y: 2, r: 2.8, col: '#e11d48' }]
    },
    {
      bx: 1235, by: 458,
      stems: [{ sx: -16, sy: 4, mx: 0, my: -4, ex: 16, ey: 1 }],
      leaves: [{ lx: -11, ly: 1, rot: -0.3 }, { lx: 0, ly: -5, rot: 0.35 }, { lx: 10, ly: 0, rot: -0.25 }],
      berries: [{ x: -13, y: 3, r: 2.7, col: '#be123c' }, { x: -2, y: -4, r: 3.0, col: '#e11d48' }, { x: 6, y: -2, r: 2.5, col: '#f43f5e' }, { x: 15, y: 2, r: 2.9, col: '#be123c' }]
    }
  ];

  for (const c of BERRY_CLUSTERS) {
    ctx.save();
    ctx.translate(c.bx, c.by);
    ctx.strokeStyle = '#18301d';
    ctx.lineWidth = 1.3;
    ctx.lineCap = 'round';
    for (const s of c.stems) {
      ctx.beginPath();
      ctx.moveTo(s.sx, s.sy);
      ctx.quadraticCurveTo(s.mx, s.my, s.ex, s.ey);
      ctx.stroke();
    }
    ctx.fillStyle = '#15803d';
    ctx.strokeStyle = '#14532d';
    ctx.lineWidth = 0.8;
    for (const lf of c.leaves) {
      ctx.save();
      ctx.translate(lf.lx, lf.ly);
      ctx.rotate(lf.rot);
      ctx.beginPath();
      ctx.ellipse(0, 0, 4.2, 2.1, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
    for (const b of c.berries) {
      ctx.fillStyle = 'rgba(5, 12, 8, 0.65)';
      ctx.beginPath();
      ctx.ellipse(b.x + 0.8, b.y + b.r * 0.8, b.r * 0.9, b.r * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();

      const bGrad = ctx.createRadialGradient(b.x - b.r * 0.35, b.y - b.r * 0.35, 0.4, b.x, b.y, b.r);
      bGrad.addColorStop(0, b.col);
      bGrad.addColorStop(0.7, '#881337');
      bGrad.addColorStop(1, '#4c0519');
      ctx.fillStyle = bGrad;
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.82)';
      ctx.beginPath();
      ctx.arc(b.x - b.r * 0.38, b.y - b.r * 0.38, 0.75, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  const PEBBLE_GROUPS = [
    { px: 338, py: 278, items: [{ rx: -8, ry: 2, w: 7, h: 4, rot: 0.2 }, { rx: -1, ry: -2, w: 9, h: 5.5, rot: -0.15 }, { rx: 8, ry: 3, w: 6, h: 3.8, rot: 0.35 }, { rx: 14, ry: 0, w: 8, h: 4.5, rot: -0.25 }] },
    { px: 618, py: 342, items: [{ rx: -12, ry: 1, w: 8, h: 5, rot: -0.2 }, { rx: -3, ry: -3, w: 10, h: 6, rot: 0.1 }, { rx: 6, ry: 2, w: 7, h: 4.2, rot: -0.3 }, { rx: 14, ry: -1, w: 6, h: 3.5, rot: 0.4 }, { rx: 19, ry: 4, w: 5, h: 3, rot: -0.15 }] },
    { px: 955, py: 378, items: [{ rx: -9, ry: 0, w: 9, h: 5.2, rot: 0.25 }, { rx: 1, ry: -3, w: 7, h: 4, rot: -0.1 }, { rx: 9, ry: 2, w: 8, h: 4.8, rot: 0.3 }] },
    { px: 1195, py: 312, items: [{ rx: -10, ry: 2, w: 8, h: 4.8, rot: -0.35 }, { rx: -1, ry: -2, w: 11, h: 6.2, rot: 0.15 }, { rx: 8, ry: 3, w: 7, h: 4.5, rot: -0.1 }, { rx: 15, ry: 0, w: 6, h: 3.8, rot: 0.3 }] },
    { px: 685, py: 465, items: [{ rx: -7, ry: 1, w: 9, h: 5.5, rot: 0.18 }, { rx: 3, ry: -2, w: 11, h: 6.5, rot: -0.22 }, { rx: 12, ry: 2, w: 7, h: 4.2, rot: 0.3 }] }
  ];

  for (const g of PEBBLE_GROUPS) {
    ctx.save();
    ctx.translate(g.px, g.py);
    for (const p of g.items) {
      ctx.save();
      ctx.translate(p.rx, p.ry);
      ctx.rotate(p.rot);
      ctx.fillStyle = 'rgba(4, 9, 6, 0.7)';
      ctx.beginPath();
      ctx.ellipse(0.6, p.h * 0.45, p.w * 0.52, p.h * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();

      const pGrad = ctx.createLinearGradient(0, -p.h * 0.5, 0, p.h * 0.5);
      pGrad.addColorStop(0, '#263a2b');
      pGrad.addColorStop(0.5, '#16241b');
      pGrad.addColorStop(1, '#0c1610');
      ctx.fillStyle = pGrad;
      ctx.beginPath();
      ctx.ellipse(0, 0, p.w * 0.5, p.h * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.ellipse(0, -p.h * 0.12, p.w * 0.36, p.h * 0.26, 0, Math.PI * 1.1, Math.PI * 1.9);
      ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  }

  const ROOTLETS = [
    { x: 440, y: 390, paths: [[[0, 0], [15, 8], [32, 12], [48, 22]], [[15, 8], [24, 18], [38, 24]]] },
    { x: 780, y: 350, paths: [[[0, 0], [18, 6], [35, 14], [52, 18]], [[18, 6], [28, 14], [40, 20]]] },
    { x: 1000, y: 410, paths: [[[0, 0], [16, 7], [30, 16], [46, 22]], [[30, 16], [38, 24], [50, 28]]] }
  ];
  ctx.strokeStyle = '#152417';
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';
  for (const rt of ROOTLETS) {
    for (const pts of rt.paths) {
      ctx.beginPath();
      ctx.moveTo(rt.x + pts[0][0], rt.y + pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(rt.x + pts[i][0], rt.y + pts[i][1]);
      ctx.stroke();
    }
  }

  ctx.save();
  ctx.translate(998, 442);
  ctx.rotate(0.35);
  ctx.fillStyle = 'rgba(3, 8, 5, 0.7)';
  ctx.beginPath();
  ctx.ellipse(1, 1, 9, 7, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#3a4a3e';
  ctx.beginPath();
  ctx.ellipse(0, 0, 8.5, 6.5, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = '#18241b';
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  for (let a = 0; a < Math.PI * 3.5; a += 0.2) {
    const rad = 1.2 + a * 0.65;
    const ax = Math.cos(a) * rad;
    const ay = Math.sin(a) * rad * 0.75;
    if (a === 0) ctx.moveTo(ax, ay);
    else ctx.lineTo(ax, ay);
  }
  ctx.stroke();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.ellipse(0, -1, 7.5, 5.0, 0, Math.PI * 1.2, Math.PI * 1.9);
  ctx.stroke();
  ctx.restore();

  ctx.restore();
}

// --- DYNAMIC DRAWING HELPERS ---

function drawStarsDynamic(ctx, t, quality) {
  if (getQualityScale(quality) === 0) return;
  const qScale = getQualityScale(quality);

  for (const s of STARFIELD) {
    const pulse = 0.75 + 0.25 * Math.sin(t * s.pulseSpeed + s.phase);
    const alpha = s.alpha * pulse;
    ctx.fillStyle = `rgba(230, 245, 240, ${alpha})`;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
    ctx.fill();

    if (qScale === 1 && s.r > 1.3) {
      ctx.fillStyle = `rgba(167, 243, 208, ${alpha * 0.35})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r * 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

let _swampMoonHaloGrad = null;
let _swampMoonSHaloGrad = null;

function getSwampMoonHaloGrad(ctx, mx, my, mr) {
  if (!_swampMoonHaloGrad) {
    _swampMoonHaloGrad = ctx.createRadialGradient(mx, my, mr * 0.6, mx, my, mr * 3.8);
    _swampMoonHaloGrad.addColorStop(0, 'rgba(215, 245, 235, 0.28)');
    _swampMoonHaloGrad.addColorStop(0.35, 'rgba(167, 243, 208, 0.12)');
    _swampMoonHaloGrad.addColorStop(0.7, 'rgba(52, 211, 153, 0.04)');
    _swampMoonHaloGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  }
  return _swampMoonHaloGrad;
}

function getSwampMoonSHaloGrad(ctx, sx, sy, sr) {
  if (!_swampMoonSHaloGrad) {
    _swampMoonSHaloGrad = ctx.createRadialGradient(sx, sy, sr * 0.5, sx, sy, sr * 2.8);
    _swampMoonSHaloGrad.addColorStop(0, 'rgba(254, 243, 199, 0.22)');
    _swampMoonSHaloGrad.addColorStop(0.5, 'rgba(251, 191, 36, 0.06)');
    _swampMoonSHaloGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  }
  return _swampMoonSHaloGrad;
}

function drawMoonsDynamic(ctx, t) {
  const mx = 960;
  const my = 90;
  const mr = 56;
  const flicker = 0.92 + 0.08 * Math.sin(t * 1.4);

  ctx.save();
  ctx.globalAlpha = flicker;
  ctx.fillStyle = getSwampMoonHaloGrad(ctx, mx, my, mr);
  ctx.beginPath();
  ctx.arc(mx, my, mr * 3.8, 0, Math.PI * 2);
  ctx.fill();

  const sx = 840;
  const sy = 52;
  const sr = 28;

  ctx.fillStyle = getSwampMoonSHaloGrad(ctx, sx, sy, sr);
  ctx.beginPath();
  ctx.arc(sx, sy, sr * 2.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawVolumetricGodRays() every
// single frame; its content never varies at runtime (same convention as
// STRAND_CONFIGS/FOAM_TONGUES/ALL_CANOPY_LEAVES elsewhere in this file).
const RAYS = Object.freeze([
  { topOff: -45, topW: 18, botOff: -320, botW: 140, alpha: 0.045 },
  { topOff: -15, topW: 24, botOff: -120, botW: 190, alpha: 0.070 },
  { topOff: 20,  topW: 30, botOff: 80,   botW: 230, alpha: 0.085 },
  { topOff: 50,  topW: 22, botOff: 280,  botW: 170, alpha: 0.055 },
  { topOff: 75,  topW: 16, botOff: 440,  botW: 130, alpha: 0.035 }
]);

function drawVolumetricGodRays(ctx, t, quality) {
  if (getQualityScale(quality) === 0) return;

  ctx.save();
  ctx.globalCompositeOperation = 'screen';

  const mx = 960;
  const my = 90;
  const rayPulse = 0.85 + 0.15 * Math.sin(t * 1.6) + 0.05 * Math.sin(t * 3.8);
  const targetY = LANE_Y + 180;

  for (const r of RAYS) {
    const topX = mx + r.topOff;
    const botX = mx + r.botOff;

    if (!r._grad) {
      r._grad = ctx.createLinearGradient(topX, my, botX, targetY);
      r._grad.addColorStop(0, `rgba(215, 255, 235, ${r.alpha * 1.5})`);
      r._grad.addColorStop(0.3, `rgba(167, 243, 208, ${r.alpha})`);
      r._grad.addColorStop(0.7, `rgba(52, 211, 153, ${r.alpha * 0.4})`);
      r._grad.addColorStop(1, 'rgba(52, 211, 153, 0)');
    }

    ctx.save();
    ctx.globalAlpha = rayPulse;
    ctx.fillStyle = r._grad;
    ctx.beginPath();
    ctx.moveTo(topX - r.topW * 0.5, my);
    ctx.lineTo(topX + r.topW * 0.5, my);
    ctx.lineTo(botX + r.botW * 0.5, targetY);
    ctx.lineTo(botX - r.botW * 0.5, targetY);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawBackgroundMistWisps() every
// single frame; its content never varies at runtime.
const BG_MIST = Object.freeze([
  { baseY: 148, ampY: 5, speed: 10, h: 30, alpha: 0.12, col: 'rgba(52, 211, 153, ' },
  { baseY: 168, ampY: 7, speed: -14, h: 38, alpha: 0.15, col: 'rgba(45, 185, 145, ' },
  { baseY: 188, ampY: 9, speed: 18, h: 44, alpha: 0.18, col: 'rgba(20, 80, 60, ' }
]);

const _bgMistGrads = [];
function getBgMistGrad(ctx, i, m) {
  if (!_bgMistGrads[i]) {
    const grad = ctx.createRadialGradient(0, 0, 25, 0, 0, 320);
    grad.addColorStop(0, `${m.col}${m.alpha})`);
    grad.addColorStop(0.55, `${m.col}${m.alpha * 0.45})`);
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    _bgMistGrads[i] = grad;
  }
  return _bgMistGrads[i];
}

function drawBackgroundMistWisps(ctx, t, quality) {
  if (getQualityScale(quality) === 0) return;

  ctx.save();
  for (let i = 0; i < BG_MIST.length; i++) {
    const m = BG_MIST[i];
    const shift = (t * m.speed) % 1200;
    const waveY = m.baseY + Math.sin(t * 0.75 + m.baseY * 0.1) * m.ampY;
    const grad = getBgMistGrad(ctx, i, m);

    for (let bx = -1200; bx < WIDTH + 1200; bx += 600) {
      const x = bx + shift;
      ctx.save();
      ctx.translate(x + 300, waveY);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(0, 0, 320, m.h, 0.04 * Math.sin(x * 0.01), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
  ctx.restore();
}

// Per-strand/per-tongue foam configs for drawWaterfallTorrent() below -
// hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) instead
// of being re-literal'd as a fresh array every single frame; their content
// never varies at runtime. See getQualityScale() near the top of this file
// for the medium-tier step-2 thinning applied when consuming these below.
const STRAND_CONFIGS = Object.freeze([
  { normX: -0.38, speed: 155, amp: 2.2, phase: 0.2, w: 1.6, col: 'rgba(255, 255, 255, 0.95)' },
  { normX: -0.26, speed: 180, amp: 1.8, phase: 1.4, w: 1.8, col: 'rgba(224, 254, 249, 0.92)' },
  { normX: -0.15, speed: 195, amp: 2.5, phase: 2.1, w: 2.2, col: 'rgba(255, 255, 255, 0.98)' },
  { normX: -0.06, speed: 215, amp: 1.6, phase: 0.8, w: 2.4, col: 'rgba(255, 255, 255, 1.0)' },
  { normX:  0.05, speed: 220, amp: 2.0, phase: 3.2, w: 2.5, col: 'rgba(255, 255, 255, 1.0)' },
  { normX:  0.16, speed: 190, amp: 1.7, phase: 1.9, w: 2.1, col: 'rgba(240, 253, 250, 0.96)' },
  { normX:  0.28, speed: 175, amp: 2.3, phase: 2.7, w: 1.8, col: 'rgba(207, 250, 254, 0.92)' },
  { normX:  0.39, speed: 150, amp: 1.9, phase: 0.5, w: 1.5, col: 'rgba(255, 255, 255, 0.90)' },
  { normX: -0.32, speed: 140, amp: 2.4, phase: 1.1, w: 2.6, col: 'rgba(6, 182, 212, 0.70)' },
  { normX: -0.10, speed: 165, amp: 2.1, phase: 2.9, w: 3.0, col: 'rgba(14, 165, 233, 0.75)' },
  { normX:  0.10, speed: 170, amp: 2.2, phase: 0.7, w: 3.0, col: 'rgba(14, 165, 233, 0.75)' },
  { normX:  0.31, speed: 145, amp: 2.0, phase: 2.3, w: 2.6, col: 'rgba(6, 182, 212, 0.70)' },
  { normX: -0.02, speed: 230, amp: 1.4, phase: 1.7, w: 1.6, col: '#ffffff' },
  { normX:  0.08, speed: 225, amp: 1.5, phase: 3.5, w: 1.6, col: '#ffffff' }
]);

const FOAM_TONGUES = Object.freeze([
  { normX: -0.22, speed: 160, len: 18, w: 3.2, seed: 0.1 },
  { normX: -0.08, speed: 200, len: 24, w: 3.8, seed: 0.35 },
  { normX:  0.06, speed: 210, len: 26, w: 4.0, seed: 0.62 },
  { normX:  0.22, speed: 170, len: 20, w: 3.4, seed: 0.84 },
  { normX: -0.15, speed: 185, len: 22, w: 3.6, seed: 0.48 },
  { normX:  0.15, speed: 195, len: 23, w: 3.6, seed: 0.22 },
  { normX: -0.30, speed: 145, len: 16, w: 2.8, seed: 0.72 },
  { normX:  0.28, speed: 155, len: 17, w: 3.0, seed: 0.95 }
]);

// Big splash-cloud configs near the torrent's base, hoisted to module scope
// and frozen for the same reason as STRAND_CONFIGS/FOAM_TONGUES above (was a
// fresh literal array re-allocated inside drawWaterfallTorrent() every
// single frame; content never varies at runtime).
const BIG_GISCHT_CLOUDS = Object.freeze([
  { ox: 0,   oy: -6,  rx: 72, ry: 32, rot: 0.0,  pulseRate: 2.1, maxAlpha: 0.88 },
  { ox: -32, oy: -10, rx: 50, ry: 26, rot: -0.2, pulseRate: 1.8, maxAlpha: 0.72 },
  { ox: 36,  oy: -14, rx: 58, ry: 28, rot: 0.22, pulseRate: 2.4, maxAlpha: 0.75 },
  { ox: 8,   oy: -28, rx: 62, ry: 34, rot: 0.05, pulseRate: 1.6, maxAlpha: 0.55 },
  { ox: 14,  oy: -18, rx: 90, ry: 42, rot: 0.1,  pulseRate: 1.3, maxAlpha: 0.38 }
]);

// The torrent's 5 body/flume gradients below anchor on hillPeakX/hillPeakY-
// derived coordinates that never change (getHorizonY() is a pure function
// of the fixed x=244, no `t` involved) and their color stops carry no `t`
// either - only the bezier/quad SHAPES drawn with them wiggle per-frame via
// getEdgeOffsetL/R(t). A CanvasGradient is just its two anchor points + color
// stops, so it's safe to build once and reuse forever even though the path
// filled with it changes shape every frame (same reasoning as
// Renderer._cachedGradient() for the Human bunker platform,
// docs/260906_performanceimplementation_plan.md Pillar 2). srcGlow/lipGrad
// stay live below - their color-stop alpha genuinely pulses with `t`.
let _waterfallGradCache = null;
function getWaterfallGradients(ctx, wx, topY, ledge1Y, ledge2Y, botY) {
  if (_waterfallGradCache) return _waterfallGradCache;

  const t1Grad = ctx.createLinearGradient(wx, topY, wx, ledge1Y);
  t1Grad.addColorStop(0, 'rgba(186, 230, 253, 0.96)');
  t1Grad.addColorStop(0.35, 'rgba(56, 189, 248, 0.92)');
  t1Grad.addColorStop(0.8, 'rgba(14, 165, 233, 0.94)');
  t1Grad.addColorStop(1, 'rgba(2, 132, 199, 0.96)');

  const t2Grad = ctx.createLinearGradient(wx, ledge1Y, wx, ledge2Y);
  t2Grad.addColorStop(0, 'rgba(207, 250, 254, 0.97)');
  t2Grad.addColorStop(0.4, 'rgba(56, 189, 248, 0.93)');
  t2Grad.addColorStop(1, 'rgba(14, 165, 233, 0.95)');

  const t3Grad = ctx.createLinearGradient(wx, ledge2Y, wx, botY);
  t3Grad.addColorStop(0, 'rgba(224, 254, 249, 0.98)');
  t3Grad.addColorStop(0.4, 'rgba(34, 211, 238, 0.94)');
  t3Grad.addColorStop(0.85, 'rgba(14, 165, 233, 0.96)');
  t3Grad.addColorStop(1, 'rgba(2, 132, 199, 0.98)');

  const leftFlumeGrad = ctx.createLinearGradient(wx - 20, 88, wx - 14, ledge2Y);
  leftFlumeGrad.addColorStop(0, 'rgba(165, 243, 252, 0.85)');
  leftFlumeGrad.addColorStop(0.5, 'rgba(56, 189, 248, 0.75)');
  leftFlumeGrad.addColorStop(1, 'rgba(14, 165, 233, 0.85)');

  const rightFlumeGrad = ctx.createLinearGradient(wx + 12, 102, wx + 24, botY - 8);
  rightFlumeGrad.addColorStop(0, 'rgba(207, 250, 254, 0.9)');
  rightFlumeGrad.addColorStop(0.5, 'rgba(34, 211, 238, 0.78)');
  rightFlumeGrad.addColorStop(1, 'rgba(6, 182, 212, 0.85)');

  const srcGlow = ctx.createRadialGradient(wx, topY, 4, wx, topY, 36);
  srcGlow.addColorStop(0, 'rgba(103, 232, 249, 0.92)');
  srcGlow.addColorStop(0.45, 'rgba(52, 211, 153, 0.48)');
  srcGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');

  const weirW = 20;
  const lipGrad = ctx.createLinearGradient(wx - weirW * 0.5, topY - 3, wx + weirW * 0.5, topY + 4);
  lipGrad.addColorStop(0, 'rgba(56, 189, 248, 0.85)');
  lipGrad.addColorStop(0.25, 'rgba(165, 243, 252, 0.98)');
  lipGrad.addColorStop(0.5, '#ffffff');
  lipGrad.addColorStop(0.75, 'rgba(165, 243, 252, 0.98)');
  lipGrad.addColorStop(1, 'rgba(56, 189, 248, 0.85)');

  const l1FoamGrad = ctx.createRadialGradient(wx, ledge1Y, 2, wx, ledge1Y, 18);
  l1FoamGrad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
  l1FoamGrad.addColorStop(0.5, 'rgba(207, 250, 254, 0.85)');
  l1FoamGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');

  const l2FoamGrad = ctx.createRadialGradient(wx, ledge2Y, 3, wx, ledge2Y, 26);
  l2FoamGrad.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
  l2FoamGrad.addColorStop(0.45, 'rgba(207, 250, 254, 0.88)');
  l2FoamGrad.addColorStop(1, 'rgba(34, 211, 238, 0)');

  _waterfallGradCache = { t1Grad, t2Grad, t3Grad, leftFlumeGrad, rightFlumeGrad, srcGlow, lipGrad, l1FoamGrad, l2FoamGrad };
  return _waterfallGradCache;
}

function drawWaterfallTorrent(ctx, t, quality) {
  const hillPeakX = 244;
  const hillPeakY = 66;
  const fallBaseY = getHorizonY(hillPeakX) + 2;

  const wx = hillPeakX + 3;
  const topY = hillPeakY;
  const ledge1Y = hillPeakY + 40;
  const ledge2Y = hillPeakY + 76;
  const botY = fallBaseY;
  const grads = getWaterfallGradients(ctx, wx, topY, ledge1Y, ledge2Y, botY);

  ctx.save();

  const pulse = 0.88 + 0.12 * Math.sin(t * 2.2);
  ctx.save();
  ctx.globalAlpha = pulse;
  ctx.fillStyle = grads.srcGlow;
  ctx.beginPath();
  ctx.arc(wx, topY, 36, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const weirW = 20;
  const weirPulse = 0.95 + 0.05 * Math.sin(t * 3.4);
  ctx.fillStyle = grads.lipGrad;
  ctx.beginPath();
  ctx.ellipse(wx, topY, (weirW * 0.5) * weirPulse, 3.8, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.ellipse(wx, topY - 0.9, weirW * 0.38, 1.8, 0, Math.PI * 0.8, Math.PI * 2.2);
  ctx.stroke();

  const getEdgeOffsetL = (y) => Math.sin(y * 0.14 - t * 11.5) * 1.6 + Math.cos(y * 0.28 + t * 6.0) * 0.8;
  const getEdgeOffsetR = (y) => Math.cos(y * 0.16 - t * 13.0) * 1.6 + Math.sin(y * 0.25 + t * 7.5) * 0.8;

  const t1TopW = 16;
  const t1BotW = 26;
  ctx.fillStyle = grads.t1Grad;
  ctx.beginPath();
  ctx.moveTo(wx - t1TopW * 0.5, topY);
  ctx.bezierCurveTo(wx - t1TopW * 0.48 + getEdgeOffsetL(78), 78, wx - t1BotW * 0.48 + getEdgeOffsetL(96), 96, wx - t1BotW * 0.5, ledge1Y);
  ctx.lineTo(wx + t1BotW * 0.5, ledge1Y);
  ctx.bezierCurveTo(wx + t1BotW * 0.48 + getEdgeOffsetR(96), 96, wx + t1TopW * 0.48 + getEdgeOffsetR(78), 78, wx + t1TopW * 0.5, topY);
  ctx.closePath();
  ctx.fill();

  const t2TopW = 27;
  const t2BotW = 39;
  ctx.fillStyle = grads.t2Grad;
  ctx.beginPath();
  ctx.moveTo(wx - t2TopW * 0.5, ledge1Y);
  ctx.bezierCurveTo(wx - t2TopW * 0.46 + getEdgeOffsetL(118), 118, wx - t2BotW * 0.48 + getEdgeOffsetL(132), 132, wx - t2BotW * 0.5, ledge2Y);
  ctx.lineTo(wx + t2BotW * 0.5, ledge2Y);
  ctx.bezierCurveTo(wx + t2BotW * 0.48 + getEdgeOffsetR(132), 132, wx + t2TopW * 0.46 + getEdgeOffsetR(118), 118, wx + t2TopW * 0.5, ledge1Y);
  ctx.closePath();
  ctx.fill();

  const t3TopW = 38;
  const t3BotW = 54;
  ctx.fillStyle = grads.t3Grad;
  ctx.beginPath();
  ctx.moveTo(wx - t3TopW * 0.5, ledge2Y);
  ctx.bezierCurveTo(wx - t3TopW * 0.46 + getEdgeOffsetL(156), 156, wx - t3BotW * 0.48 + getEdgeOffsetL(172), 172, wx - t3BotW * 0.5, botY);
  ctx.lineTo(wx + t3BotW * 0.5, botY);
  ctx.bezierCurveTo(wx + t3BotW * 0.48 + getEdgeOffsetR(172), 172, wx + t3TopW * 0.46 + getEdgeOffsetR(156), 156, wx + t3TopW * 0.5, ledge2Y);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = grads.leftFlumeGrad;
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(wx - 12, 78);
  ctx.quadraticCurveTo(wx - 22 + Math.sin(t * 8.0) * 1.5, 108, wx - 16, ledge2Y + 6);
  ctx.stroke();

  ctx.strokeStyle = grads.rightFlumeGrad;
  ctx.lineWidth = 2.8;
  ctx.beginPath();
  ctx.moveTo(wx + 14, 98);
  ctx.quadraticCurveTo(wx + 26 + Math.cos(t * 7.5) * 1.8, 136, wx + 20, botY);
  ctx.stroke();

  // Medium tier draws every other strand/foam-tongue instead of the full
  // set (Paradigm 10: Multi-Tier Quality-Gating) - high/ultra unchanged.
  // quality is never 'low' here: renderSwampDynamicBackground() no longer
  // calls this function at all on that tier, see its own comment below.
  const detailStep = quality === 'medium' ? 2 : 1;

  ctx.lineCap = 'round';
  for (let sIdx = 0; sIdx < STRAND_CONFIGS.length; sIdx += detailStep) {
    const sc = STRAND_CONFIGS[sIdx];
    ctx.strokeStyle = sc.col;
    ctx.lineWidth = sc.w;
    ctx.beginPath();
    let started = false;
    for (let sy = topY; sy <= botY; sy += 8) {
      const frac = (sy - topY) / (botY - topY);
      const cascadeW = t1TopW * (1 - frac) + t3BotW * frac;
      const wave = Math.sin(sy * 0.08 - t * (sc.speed * 0.06) + sc.phase) * sc.amp;
      const sx = wx + sc.normX * cascadeW + wave;
      if (!started) {
        ctx.moveTo(sx, sy);
        started = true;
      } else {
        ctx.lineTo(sx, sy);
      }
    }
    ctx.stroke();
  }

  for (let ftIdx = 0; ftIdx < FOAM_TONGUES.length; ftIdx += detailStep) {
    const ft = FOAM_TONGUES[ftIdx];
    const totalDist = botY - topY;
    const dist = (t * ft.speed + ft.seed * totalDist) % totalDist;
    const curY = topY + dist;
    const frac = dist / totalDist;
    const cascadeW = t1TopW * (1 - frac) + t3BotW * frac;
    const curX = wx + ft.normX * cascadeW + Math.sin(curY * 0.1 - t * 8.0) * 1.5;

    if (!ft._grad) {
      ft._grad = ctx.createLinearGradient(0, -ft.len * 0.5, 0, ft.len * 0.5);
      ft._grad.addColorStop(0, 'rgba(255, 255, 255, 0)');
      ft._grad.addColorStop(0.4, 'rgba(255, 255, 255, 0.95)');
      ft._grad.addColorStop(0.7, 'rgba(207, 250, 254, 0.85)');
      ft._grad.addColorStop(1, 'rgba(103, 232, 249, 0)');
    }

    ctx.save();
    ctx.translate(curX, curY);
    ctx.strokeStyle = ft._grad;
    ctx.lineWidth = ft.w * (0.8 + 0.4 * frac);
    ctx.beginPath();
    ctx.moveTo(-0.5, -ft.len * 0.5);
    ctx.quadraticCurveTo(Math.sin(t * 10 + ft.seed) * 1.2, 0, 0.5, ft.len * 0.5);
    ctx.stroke();
    ctx.restore();
  }

  const l1Pulse = 0.88 + 0.12 * Math.sin(t * 5.2);
  ctx.save();
  ctx.globalAlpha = l1Pulse;
  ctx.fillStyle = grads.l1FoamGrad;
  ctx.beginPath();
  ctx.ellipse(wx, ledge1Y, 17 * l1Pulse, 5.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  if (getQualityScale(quality) > 0) {
    ctx.fillStyle = `rgba(240, 253, 250, ${0.82 * l1Pulse})`;
    ctx.beginPath();
    ctx.ellipse(wx - t1BotW * 0.62, ledge1Y - 1, 13, 6, -0.32, 0, Math.PI * 2);
    ctx.ellipse(wx + t1BotW * 0.62, ledge1Y - 1, 13, 6, 0.32, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    for (let d = -2; d <= 2; d++) {
      const dPhase = (t * 8.0 + d * 1.7) % 1.0;
      const dx = wx + d * 8 + (d < 0 ? -6 : 6) * dPhase;
      const dy = ledge1Y - Math.sin(dPhase * Math.PI) * 9 + dPhase * 3;
      ctx.fillRect(dx - 0.8, dy - 0.8, 1.6, 1.6);
    }
  }

  const l2Pulse = 0.86 + 0.14 * Math.sin(t * 4.6 + 1.2);
  ctx.save();
  ctx.globalAlpha = l2Pulse;
  ctx.fillStyle = grads.l2FoamGrad;
  ctx.beginPath();
  ctx.ellipse(wx, ledge2Y, 25 * l2Pulse, 6.8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  if (getQualityScale(quality) > 0) {
    ctx.fillStyle = `rgba(240, 253, 250, ${0.85 * l2Pulse})`;
    ctx.beginPath();
    ctx.ellipse(wx - t2BotW * 0.66, ledge2Y - 1, 17, 7.5, -0.34, 0, Math.PI * 2);
    ctx.ellipse(wx + t2BotW * 0.66, ledge2Y - 1, 17, 7.5, 0.34, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    for (let d = -3; d <= 3; d++) {
      if (d === 0) continue;
      const dPhase = (t * 7.5 + d * 1.4) % 1.0;
      const dx = wx + d * 9 + (d < 0 ? -8 : 8) * dPhase;
      const dy = ledge2Y - Math.sin(dPhase * Math.PI) * 12 + dPhase * 4;
      ctx.fillRect(dx - 0.9, dy - 0.9, 1.8, 1.8);
    }
  }

  ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
  ctx.beginPath();
  ctx.moveTo(wx - t3BotW * 0.52, botY - 1);
  for (let tooth = -5; tooth <= 5; tooth++) {
    const tx = wx + tooth * 5.2;
    const tDepth = botY + 3 + Math.sin(t * 12 + tooth * 1.8) * 3.5 + 4;
    ctx.lineTo(tx, tDepth);
  }
  ctx.lineTo(wx + t3BotW * 0.52, botY - 1);
  ctx.closePath();
  ctx.fill();

  // Medium tier renders every other cloud (5 -> 3: indices 0/2/4) instead of
  // all 5, each of which otherwise costs its own createRadialGradient() -
  // same detailStep-driven thinning as the strands/foam-tongues above
  // (Paradigm 10: Multi-Tier Quality-Gating).
  const gischtCanvas = getGischtCloudCanvas();
  for (let gcIdx = 0; gcIdx < BIG_GISCHT_CLOUDS.length; gcIdx += detailStep) {
    const gc = BIG_GISCHT_CLOUDS[gcIdx];
    const cp = 0.85 + 0.15 * Math.sin(t * gc.pulseRate + gc.ox);
    if (gischtCanvas) {
      ctx.save();
      ctx.translate(wx + gc.ox, botY + gc.oy);
      if (gc.rot) ctx.rotate(gc.rot);
      ctx.globalAlpha *= (gc.maxAlpha * cp);
      ctx.drawImage(gischtCanvas, -gc.rx, -gc.ry, gc.rx * 2, gc.ry * 2);
      ctx.restore();
    }
  }

  ctx.fillStyle = `rgba(248, 250, 252, ${0.95 * pulse})`;
  ctx.beginPath();
  ctx.ellipse(wx, botY + 1, 44, 14, 0, 0, Math.PI * 2);
  ctx.fill();

  if (getQualityScale(quality) > 0) {
    for (let rIdx = 0; rIdx < 5; rIdx++) {
      const foamCycle = (t * 1.5 + rIdx * 0.42) % 2.1;
      const foamR = 10 + foamCycle * 24;
      const foamA = (1.0 - foamCycle / 2.1) * 0.72;
      ctx.strokeStyle = `rgba(240, 253, 250, ${foamA})`;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.ellipse(wx, botY + 2, foamR, foamR * 0.38, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Medium tier draws roughly half the splash particles (same detailStep
    // thinning as the strands/foam-tongues/gischt-clouds above) instead of
    // the full 50 (Paradigm 10: Multi-Tier Quality-Gating).
    for (let pIdx = 0; pIdx < 50; pIdx += detailStep) {
      const pPeriod = 1.8 + ((pIdx * 17) % 15) * 0.08;
      const pPhase = ((pIdx * 23) % 31) * 0.1;
      const pAge = ((t + pPhase) % pPeriod) / pPeriod;

      if (pIdx < 12) {
        if (gischtCanvas) {
          const spawnX = wx + ((pIdx * 11) % 36) - 18;
          const driftX = spawnX + pAge * 22 + Math.sin(t * 1.8 + pIdx) * 8;
          const riseY = botY - pAge * 48;
          const pSize = 4.5 + pAge * 5.5;
          const pAlpha = Math.sin(pAge * Math.PI) * 0.68;

          ctx.save();
          ctx.globalAlpha *= pAlpha;
          ctx.drawImage(gischtCanvas, driftX - pSize, riseY - pSize, pSize * 2, pSize * 2);
          ctx.restore();
        }
      } else if (pIdx < 32) {
        const sideDir = (pIdx % 2 === 0) ? 1 : -1;
        const spreadRate = 12 + ((pIdx * 7) % 18);
        const dropX = wx + sideDir * (pAge * spreadRate) + (pAge * 14);
        const dropY = botY - Math.sin(pAge * Math.PI * 0.85) * (26 + ((pIdx * 5) % 16));
        const dropR = 1.8 + ((pIdx * 3) % 4) * 0.4;
        const dropA = (1.0 - pAge) * 0.88;

        ctx.fillStyle = `rgba(224, 254, 249, ${dropA})`;
        ctx.beginPath();
        ctx.arc(dropX, dropY, dropR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(255, 255, 255, ${dropA * 0.9})`;
        ctx.beginPath();
        ctx.arc(dropX - dropR * 0.3, dropY - dropR * 0.3, dropR * 0.4, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const mistX = wx + ((pIdx * 13) % 44) - 16 + pAge * 36 + Math.sin(t * 3.5 + pIdx) * 12;
        const mistY = botY - pAge * 64;
        const mistR = 0.9 + ((pIdx * 5) % 3) * 0.4;
        const mistA = Math.sin(pAge * Math.PI) * 0.75;

        ctx.fillStyle = `rgba(207, 250, 254, ${mistA})`;
        ctx.beginPath();
        ctx.arc(mistX, mistY, mistR, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  ctx.strokeStyle = 'rgba(103, 232, 249, 0.7)';
  ctx.lineWidth = 3.6;
  ctx.beginPath();
  ctx.moveTo(wx, botY + 2);
  ctx.quadraticCurveTo(wx + 22, botY + 16, wx + 42, botY + 30);
  ctx.quadraticCurveTo(wx + 52, botY + 44, 310, 250);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.lineWidth = 1.4;
  ctx.stroke();

  ctx.restore();
}

function drawPerspectiveGrassTexture(ctx, t, quality) {
  const isBakedLow = quality === 'low_baked';
  if (!isBakedLow && getQualityScale(quality) === 0) return;
  const qScale = isBakedLow ? 0.5 : getQualityScale(quality);

  ctx.save();
  ctx.lineCap = 'round';

  const step = (qScale === 1 || isBakedLow) ? 1 : 2;

  // Batched draw path across buckets (reduces ~5,700 strokes to ~44 strokes)
  for (let g = 0; g < PERSPECTIVE_GRASS_BUCKETS.length; g++) {
    const bucket = PERSPECTIVE_GRASS_BUCKETS[g];
    const tufts = bucket.tufts;
    ctx.lineWidth = bucket.w;

    // Pass 1: Batched grass bases
    ctx.strokeStyle = bucket.baseCol;
    ctx.beginPath();
    for (let i = 0; i < tufts.length; i += step) {
      const tuft = tufts[i];
      const sway = t === 0 ? 0 : Math.sin(t * 2.2 + tuft.phase) * (2.8 * tuft.scale);
      const count = tuft.count;
      for (let b = 0; b < count; b++) {
        const bladeSpread = (b - (count - 1) * 0.5) * (3.8 * tuft.scale);
        const bladeH = tuft.h * (0.82 + 0.18 * Math.sin(b * 1.8 + tuft.phase));
        const bladeSway = sway * (0.8 + 0.3 * b);

        const bx = tuft.x + bladeSpread;
        const by = tuft.y;
        const tx = bx + bladeSpread * 0.7 + bladeSway;
        const ty = by - bladeH;
        const mx = bx + bladeSpread * 0.3 + bladeSway * 0.4;
        const my = by - bladeH * 0.55;

        ctx.moveTo(bx, by);
        ctx.quadraticCurveTo(mx, my, tx, ty);
      }
    }
    ctx.stroke();

    // Pass 2: Batched grass tips
    ctx.strokeStyle = bucket.tipCol;
    ctx.beginPath();
    for (let i = 0; i < tufts.length; i += step) {
      const tuft = tufts[i];
      const sway = t === 0 ? 0 : Math.sin(t * 2.2 + tuft.phase) * (2.8 * tuft.scale);
      const count = tuft.count;
      for (let b = 0; b < count; b++) {
        const bladeSpread = (b - (count - 1) * 0.5) * (3.8 * tuft.scale);
        const bladeH = tuft.h * (0.82 + 0.18 * Math.sin(b * 1.8 + tuft.phase));
        const bladeSway = sway * (0.8 + 0.3 * b);

        const bx = tuft.x + bladeSpread;
        const by = tuft.y;
        const tx = bx + bladeSpread * 0.7 + bladeSway;
        const ty = by - bladeH;
        const mx = bx + bladeSpread * 0.3 + bladeSway * 0.4;
        const my = by - bladeH * 0.55;

        ctx.moveTo(mx, my);
        ctx.lineTo(tx, ty);
      }
    }
    ctx.stroke();
  }

  ctx.restore();
}

function drawHorizonFlora(ctx, t) {
  ctx.save();
  for (const f of HORIZON_FLORA) {
    const y = getHorizonY(f.x);
    ctx.save();
    ctx.translate(f.x, y);
    ctx.scale(f.scale, f.scale);

    if (f.type === 'fern') {
      const sway = Math.sin(t * 1.8 + f.x) * 3;
      ctx.strokeStyle = f.hue;
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.quadraticCurveTo(-4 + sway * 0.4, -12, -8 + sway, -26);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.quadraticCurveTo(4 + sway * 0.4, -14, 8 + sway, -28);
      ctx.stroke();
    } else if (f.type === 'reeds') {
      const sway = Math.sin(t * 2.2 + f.x) * 4;
      ctx.strokeStyle = f.hue;
      ctx.lineWidth = 1.6;
      ctx.lineCap = 'round';
      for (let i = -1; i <= 1; i++) {
        const offX = i * 4;
        const h = 22 + i * 4;
        ctx.beginPath();
        ctx.moveTo(offX, 0);
        ctx.quadraticCurveTo(offX + sway * 0.5, -h * 0.5, offX + sway, -h);
        ctx.stroke();
      }
    } else if (f.type === 'mushroom_cluster') {
      const pulse = 0.85 + 0.15 * Math.sin(t * 2.0 + f.x);
      ctx.fillStyle = 'rgba(74, 222, 128, 0.4)';
      ctx.beginPath();
      ctx.arc(0, -10, 14 * pulse, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#1e3e2c';
      ctx.fillRect(-2, -10, 4, 10);
      ctx.fillStyle = f.glow;
      ctx.beginPath();
      ctx.ellipse(0, -10, 8, 4, 0, 0, Math.PI * 2);
      ctx.fill();
    } else if (f.type === 'alien_bulb') {
      const pulse = 0.8 + 0.2 * Math.sin(t * 2.6 + f.x);
      ctx.strokeStyle = '#27442d';
      ctx.lineWidth = 2.0;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -16);
      ctx.stroke();

      ctx.fillStyle = f.glow;
      ctx.beginPath();
      ctx.arc(0, -18, 5 * pulse, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
  ctx.restore();
}

// Cached per-palette-color radial sprites for drawDevilFlamethrowerSmoke()
// below (Paradigm 3: LUTs for Gradients). Up to 320 visible particles used
// to each build a fresh ctx.createRadialGradient() every frame from
// DEVIL_SMOKE_PALETTE's 7 fixed colors - the single biggest per-frame cost
// in this file. Same lazy module-level cache idiom as
// backgroundVoidglass.js's getBloomSprite()/getStreakSprite(): built once,
// keyed here by the palette color object itself (every particle's `col` is
// a shared reference into DEVIL_SMOKE_PALETTE, so object identity is a safe,
// allocation-free cache key - no separate color index needs threading
// through). The original gradient's inner radius is always exactly 0.15x
// its outer radius regardless of the live per-particle r, so one
// fixed-size sprite (baked at a representative reference radius) can be
// blitted at any on-screen radius via drawImage's dw/dh and stay
// geometrically identical - the same scaling technique already used by
// units.js's bakeUnitRadialGradientCanvas()-based glow textures
// (getSulDelvingPillarOcularGlowCanvas() et al.) for the same reason. The
// live per-particle/per-frame alpha (which used to be baked into each
// color-stop's rgba() template string) is applied via ctx.globalAlpha
// instead: rgba(c, stopAlpha) * globalAlpha(liveAlpha) is the exact same
// composited color as the original rgba(c, stopAlpha * liveAlpha).
const DEVIL_SMOKE_SPRITE_REF_R = 32;
let _devilSmokeSprites = null;
function getDevilSmokeSprite(col) {
  if (!_devilSmokeSprites && typeof document !== 'undefined') {
    _devilSmokeSprites = new Map();
    const size = DEVIL_SMOKE_SPRITE_REF_R * 2;
    for (const paletteCol of DEVIL_SMOKE_PALETTE) {
      const cvs = document.createElement('canvas');
      cvs.width = size;
      cvs.height = size;
      const c = cvs.getContext('2d');
      const grad = c.createRadialGradient(
        DEVIL_SMOKE_SPRITE_REF_R, DEVIL_SMOKE_SPRITE_REF_R, DEVIL_SMOKE_SPRITE_REF_R * 0.15,
        DEVIL_SMOKE_SPRITE_REF_R, DEVIL_SMOKE_SPRITE_REF_R, DEVIL_SMOKE_SPRITE_REF_R
      );
      grad.addColorStop(0, `rgba(${paletteCol.r}, ${paletteCol.g}, ${paletteCol.b}, 1)`);
      grad.addColorStop(0.55, `rgba(${paletteCol.r}, ${paletteCol.g}, ${paletteCol.b}, 0.5)`);
      grad.addColorStop(1, `rgba(${paletteCol.r}, ${paletteCol.g}, ${paletteCol.b}, 0)`);
      c.fillStyle = grad;
      c.fillRect(0, 0, size, size);
      _devilSmokeSprites.set(paletteCol, cvs);
    }
  }
  return _devilSmokeSprites ? _devilSmokeSprites.get(col) : null;
}

function drawDevilFlamethrowerSmoke(ctx, t, quality) {
  if (getQualityScale(quality) < 0.2) return;

  ctx.save();
  const PANORAMA_W = 1800;
  const q = getQualityScale(quality);
  const step = q === 1 ? 1 : (q >= 0.5 ? 2 : 3);

  for (let i = 0; i < DEVIL_SMOKE_PARTICLES.length; i += step) {
    const p = DEVIL_SMOKE_PARTICLES[i];
    const progress = ((t + p.phase) % p.life) / p.life;

    const driftX = t * p.speedX;
    const px = ((p.baseX + driftX + 100) % (PANORAMA_W + 200)) - 100;
    if (px < -50 || px > WIDTH + 50) continue;

    const py = p.baseY - progress * p.riseDist + Math.sin(t * p.wobbleSpeed + i) * p.wobbleAmp;
    const r = p.r0 + progress * (p.rMax - p.r0);

    let alpha;
    if (progress < 0.22) {
      alpha = p.maxA * (progress / 0.22);
    } else {
      const fadeProgress = (progress - 0.22) / 0.78;
      alpha = Math.max(0, p.maxA * (1.0 - fadeProgress * fadeProgress));
    }
    if (alpha <= 0.005) continue;

    const sprite = getDevilSmokeSprite(p.col);
    if (sprite) {
      ctx.globalAlpha = alpha;
      ctx.drawImage(sprite, px - r, py - r, r * 2, r * 2);
      ctx.globalAlpha = 1;
    }
  }

  if (q > 0.5) {
    const emberStep = q === 1 ? 1 : 2;
    for (let j = 0; j < DEVIL_SMOKE_EMBERS.length; j += emberStep) {
      const em = DEVIL_SMOKE_EMBERS[j];
      const progress = ((t + em.phase) % em.life) / em.life;

      const driftX = t * em.speedX;
      const ex = ((em.baseX + driftX + 100) % (PANORAMA_W + 200)) - 100;
      if (ex < -20 || ex > WIDTH + 20) continue;

      const ey = em.baseY - progress * em.riseDist + Math.sin(t * 4.0 + j) * 2.0;
      const twinkle = 0.6 + 0.4 * Math.sin(t * 16.0 + j * 2.5);
      const emAlpha = Math.sin(progress * Math.PI) * 0.75 * twinkle;
      if (emAlpha <= 0.02) continue;

      ctx.fillStyle = em.color;
      ctx.globalAlpha = emAlpha;
      ctx.fillRect(ex - em.size * 0.5, ey - em.size * 0.5, em.size, em.size);
    }
  }

  ctx.restore();
}

export function drawReflectivePuddlesStatic(ctx) {
  for (const p of PUDDLES) {
    if (p.satellites) {
      for (const sat of p.satellites) {
        const sx = p.cx + sat.dx;
        const sy = p.cy + sat.dy;

        ctx.strokeStyle = 'rgba(10, 20, 14, 0.28)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(sx, sy, sat.rx + 2, sat.ry + 1.5, 0, 0, Math.PI * 2);
        ctx.stroke();

        if (!sat._sGrad) {
          sat._sGrad = ctx.createRadialGradient(sx, sy, 1, sx, sy, sat.rx);
          sat._sGrad.addColorStop(0, '#163340');
          sat._sGrad.addColorStop(1, '#09161e');
        }
        ctx.fillStyle = sat._sGrad;
        ctx.beginPath();
        ctx.ellipse(sx, sy, sat.rx, sat.ry, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'rgba(215, 245, 235, 0.22)';
        ctx.lineWidth = 1.0;
        ctx.beginPath();
        ctx.moveTo(sx - sat.rx * 0.4, sy - sat.ry * 0.2);
        ctx.lineTo(sx + sat.rx * 0.3, sy - sat.ry * 0.2);
        ctx.stroke();
      }
    }

    ctx.save();
    ctx.translate(p.cx, p.cy);

    ctx.beginPath();
    const n0 = p.nodes[0];
    ctx.moveTo(n0.x, n0.y);
    for (let i = 0; i < p.nodes.length; i++) {
      const curr = p.nodes[i];
      const next = p.nodes[(i + 1) % p.nodes.length];
      const midX = (curr.x + next.x) * 0.5;
      const midY = (curr.y + next.y) * 0.5;
      ctx.quadraticCurveTo(curr.x, curr.y, midX, midY);
    }
    ctx.closePath();

    ctx.save();
    ctx.lineWidth = 10;
    ctx.strokeStyle = 'rgba(12, 24, 18, 0.16)';
    ctx.stroke();
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(9, 18, 13, 0.28)';
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(7, 14, 10, 0.38)';
    ctx.stroke();
    ctx.restore();

    if (!p._waterGrad) {
      p._waterGrad = ctx.createRadialGradient(0, 0, 5, 0, 0, p.rx);
      p._waterGrad.addColorStop(0, '#1c3e4e');
      p._waterGrad.addColorStop(0.55, '#132c3a');
      p._waterGrad.addColorStop(0.85, '#0d1f2a');
      p._waterGrad.addColorStop(1, '#08141d');
    }
    ctx.fillStyle = p._waterGrad;
    ctx.fill();

    ctx.save();
    ctx.clip();

    const sheenX = p.rx * 0.15;
    const sheenY = -p.ry * 0.2;
    if (!p._moonSheen) {
      p._moonSheen = ctx.createRadialGradient(sheenX, sheenY, 3, sheenX, sheenY, p.rx * 0.75);
      p._moonSheen.addColorStop(0, 'rgba(215, 245, 235, 0.42)');
      p._moonSheen.addColorStop(0.35, 'rgba(147, 197, 215, 0.24)');
      p._moonSheen.addColorStop(0.7, 'rgba(80, 140, 170, 0.08)');
      p._moonSheen.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    ctx.fillStyle = p._moonSheen;
    ctx.beginPath();
    ctx.ellipse(sheenX, sheenY, p.rx * 0.75, p.ry * 0.65, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(240, 253, 250, 0.35)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(sheenX - 14, sheenY);
    ctx.lineTo(sheenX + 16, sheenY);
    ctx.stroke();

    ctx.restore();
    ctx.restore();

    if (p.sunkenLog) {
      const sl = p.sunkenLog;
      const lx1 = p.cx + sl.x1;
      const ly1 = p.cy + sl.y1;
      const lx2 = p.cx + sl.x2;
      const ly2 = p.cy + sl.y2;

      ctx.save();
      ctx.strokeStyle = '#0e1c14';
      ctx.lineWidth = sl.thick * 2.2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(lx1, ly1);
      ctx.quadraticCurveTo((lx1 + lx2) * 0.5, (ly1 + ly2) * 0.5 - 2, lx2, ly2);
      ctx.stroke();

      ctx.strokeStyle = '#223c2a';
      ctx.lineWidth = sl.thick * 1.3;
      ctx.beginPath();
      ctx.moveTo(lx1 + 2, ly1 - 1);
      ctx.quadraticCurveTo((lx1 + lx2) * 0.5, (ly1 + ly2) * 0.5 - 3, lx2 - 2, ly2 - 1);
      ctx.stroke();

      ctx.strokeStyle = 'rgba(74, 222, 128, 0.35)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(lx1 + 5, ly1 - 2);
      ctx.lineTo((lx1 + lx2) * 0.5, (ly1 + ly2) * 0.5 - 4);
      ctx.stroke();
      ctx.restore();
    }
  }
}

export function drawReflectivePuddlesDynamic(ctx, t, quality) {
  const qScale = getQualityScale(quality);
  if (qScale <= 0) return;

  for (const p of PUDDLES) {
    const sheenX = p.cx + p.rx * 0.15;
    const sheenY = p.cy - p.ry * 0.2;

    const ripplePhase = (t * 0.6 + p.id * 1.5) % 4.0;
    const rippleR = ripplePhase * 12;
    const rippleAlpha = Math.max(0, 1 - ripplePhase / 4.0) * 0.25;

    ctx.strokeStyle = `rgba(186, 230, 253, ${rippleAlpha})`;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.ellipse(sheenX, sheenY, rippleR * 1.8, rippleR * 0.5, 0, 0, Math.PI * 2);
    ctx.stroke();

    const ripple2Phase = (t * 0.5 + p.id * 2.2 + 2.0) % 4.0;
    const ripple2R = ripple2Phase * 9;
    const ripple2Alpha = Math.max(0, 1 - ripple2Phase / 4.0) * 0.2;
    ctx.strokeStyle = `rgba(147, 197, 253, ${ripple2Alpha})`;
    ctx.beginPath();
    ctx.ellipse(sheenX - 16, sheenY + 6, ripple2R * 1.6, ripple2R * 0.45, 0.1, 0, Math.PI * 2);
    ctx.stroke();

    if (p.shoreReeds) {
      ctx.save();
      for (const sr of p.shoreReeds) {
        const rx = p.cx + sr.dx;
        const ry = p.cy + sr.dy;
        const sway = Math.sin(t * 2.0 + rx * 0.1) * 2.2;

        for (let i = 0; i < sr.count; i++) {
          const offX = (i - (sr.count - 1) * 0.5) * 3.5;
          const bladeH = sr.h * (0.8 + 0.2 * Math.sin(i * 1.5));
          ctx.strokeStyle = '#1b3824';
          ctx.lineWidth = 1.8;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(rx + offX, ry);
          ctx.quadraticCurveTo(rx + offX + sway * 0.5, ry - bladeH * 0.55, rx + offX + sway + i * 1.5, ry - bladeH);
          ctx.stroke();

          ctx.strokeStyle = '#52b788';
          ctx.lineWidth = 0.9;
          ctx.beginPath();
          ctx.moveTo(rx + offX + sway * 0.5, ry - bladeH * 0.55);
          ctx.lineTo(rx + offX + sway + i * 1.5, ry - bladeH);
          ctx.stroke();
        }
      }
      ctx.restore();
    }

    if (p.lilyPads) {
      for (const pad of p.lilyPads) {
        const px = p.cx + pad.dx;
        const py = p.cy + pad.dy;
        const r = pad.r;
        const rot = pad.rot + Math.sin(t * 0.8 + px) * 0.05;

        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(rot);

        ctx.strokeStyle = 'rgba(7, 18, 24, 0.55)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.ellipse(0, 0, r * 1.15, r * 0.65, 0, 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = '#1b432e';
        ctx.beginPath();
        ctx.ellipse(0, 0, r, r * 0.55, 0, 0.35, Math.PI * 2 - 0.35);
        ctx.lineTo(0, 0);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = 'rgba(74, 222, 128, 0.55)';
        ctx.lineWidth = 1.0;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(-r * 0.75, 0);
        ctx.stroke();

        ctx.restore();
      }
    }
  }
}

export function drawReflectivePuddles(ctx, t, quality) {
  drawReflectivePuddlesStatic(ctx);
  drawReflectivePuddlesDynamic(ctx, t, quality);
}

// Bioluminescent pinhead-mushroom colonies for drawEnrichedGroundDetailsDynamic()
// below, hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) -
// content never varies at runtime, only t-driven pulse/aura math downstream.
const PINHEAD_COLONIES = Object.freeze([
  { cx: 575, cy: 275, caps: [{ ox: -6, h: 7, r: 1.8, phase: 0.2 }, { ox: -2, h: 9, r: 2.2, phase: 1.1 }, { ox: 3, h: 6, r: 1.6, phase: 2.4 }, { ox: 7, h: 8, r: 2.0, phase: 3.5 }] },
  { cx: 812, cy: 415, caps: [{ ox: -8, h: 8, r: 2.0, phase: 0.8 }, { ox: -3, h: 11, r: 2.4, phase: 1.9 }, { ox: 2, h: 7, r: 1.7, phase: 3.1 }, { ox: 6, h: 10, r: 2.1, phase: 4.2 }, { ox: 10, h: 6, r: 1.5, phase: 5.0 }] },
  { cx: 1115, cy: 342, caps: [{ ox: -5, h: 8, r: 2.0, phase: 1.4 }, { ox: 0, h: 10, r: 2.3, phase: 2.7 }, { ox: 5, h: 7, r: 1.8, phase: 4.1 }, { ox: 9, h: 6, r: 1.5, phase: 0.3 }] },
  { cx: 1385, cy: 468, caps: [{ ox: -7, h: 7, r: 1.7, phase: 0.6 }, { ox: -2, h: 10, r: 2.2, phase: 1.8 }, { ox: 3, h: 8, r: 1.9, phase: 2.9 }, { ox: 8, h: 11, r: 2.3, phase: 4.3 }] }
]);

// Bog-strider critters for the same function, hoisted for the same reason.
const STRIDERS = Object.freeze([
  { basePoolX: 480, basePoolY: 315, speed: 1.6, ampX: 14, ampY: 5, phase: 0.5 },
  { basePoolX: 885, basePoolY: 360, speed: 1.4, ampX: 18, ampY: 6, phase: 2.2 }
]);

function drawEnrichedGroundDetailsDynamic(ctx, t, quality) {
  ctx.save();

  // Medium tier only auras every other mushroom cap (same detailStep-style
  // thinning as drawWaterfallTorrent's strands/foam-tongues/gischt-clouds/
  // splash-particles) instead of all 17 PINHEAD_COLONIES caps, each of which
  // otherwise costs its own createRadialGradient() every frame regardless
  // of tier (Paradigm 10: Multi-Tier Quality-Gating). The cap's stem stroke
  // and cap-fill ellipse below stay unthinned on every non-'low' tier - only
  // the soft bioluminescent aura gradient is gated.
  const auraStep = quality === 'medium' ? 2 : 1;

  let capIdx = 0;
  for (const col of PINHEAD_COLONIES) {
    ctx.save();
    ctx.translate(col.cx, col.cy);

    for (const m of col.caps) {
      const p = 0.82 + 0.18 * Math.sin(t * 2.5 + m.phase);
      ctx.strokeStyle = `rgba(167, 243, 208, ${0.7 * p})`;
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.moveTo(m.ox, 0);
      ctx.quadraticCurveTo(m.ox + (m.ox > 0 ? 1 : -1), -m.h * 0.5, m.ox, -m.h);
      ctx.stroke();

      if (getQualityScale(quality) > 0 && capIdx % auraStep === 0) {
        const auraCanvas = getMushroomAuraCanvas();
        const auraR = m.r * 3.5;
        if (auraCanvas) {
          ctx.save();
          ctx.globalAlpha *= p;
          ctx.drawImage(auraCanvas, m.ox - auraR, -m.h - auraR, auraR * 2, auraR * 2);
          ctx.restore();
        } else {
          const aura = ctx.createRadialGradient(m.ox, -m.h, 0, m.ox, -m.h, auraR);
          aura.addColorStop(0, `rgba(103, 232, 249, ${0.65 * p})`);
          aura.addColorStop(0.5, `rgba(52, 211, 153, ${0.25 * p})`);
          aura.addColorStop(1, 'rgba(0, 0, 0, 0)');
          ctx.fillStyle = aura;
          ctx.beginPath();
          ctx.arc(m.ox, -m.h, auraR, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      capIdx++;

      ctx.fillStyle = `rgba(207, 250, 254, ${0.92 * p})`;
      ctx.beginPath();
      ctx.ellipse(m.ox, -m.h, m.r, m.r * 0.85, 0, Math.PI, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  for (const s of STRIDERS) {
    const sx = s.basePoolX + Math.sin(t * s.speed + s.phase) * s.ampX;
    const sy = s.basePoolY + Math.cos(t * s.speed + s.phase) * s.ampY;

    ctx.save();
    ctx.translate(sx, sy);

    ctx.fillStyle = 'rgba(207, 250, 254, 0.32)';
    const footOffsets = [[-6, -4], [6, -4], [-7, 4], [7, 4]];
    for (const fo of footOffsets) {
      ctx.beginPath();
      ctx.ellipse(fo[0], fo[1], 1.6, 0.9, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = '#08120a';
    ctx.beginPath();
    ctx.ellipse(0, 0, 2.8, 1.0, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#0e1e12';
    ctx.lineWidth = 0.8;
    for (const fo of footOffsets) {
      ctx.beginPath();
      ctx.moveTo(fo[0] * 0.2, fo[1] * 0.2);
      ctx.lineTo(fo[0] * 0.6, fo[1] * 0.7 - 1.2);
      ctx.lineTo(fo[0], fo[1]);
      ctx.stroke();
    }

    ctx.restore();
  }

  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawGroundMistWisps() every
// single frame; its content never varies at runtime.
const MIST_BANDS = Object.freeze([
  { y: 310, speed: 18, baseAlpha: 0.05, h: 50 },
  { y: 410, speed: 26, baseAlpha: 0.07, h: 65 },
  { y: 490, speed: 34, baseAlpha: 0.09, h: 55 }
]);

function drawGroundMistWisps(ctx, t, quality) {
  if (getQualityScale(quality) < 0.5) return;

  for (const m of MIST_BANDS) {
    if (!m._cachedGrad) {
      m._cachedGrad = ctx.createRadialGradient(0, 0, 80, 0, 0, 450);
      m._cachedGrad.addColorStop(0, `rgba(52, 211, 153, ${m.baseAlpha})`);
      m._cachedGrad.addColorStop(0.5, `rgba(20, 84, 60, ${m.baseAlpha * 0.6})`);
      m._cachedGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    }
    const shift = (t * m.speed) % 800;
    for (let bx = -800; bx < WIDTH + 800; bx += 800) {
      const mx = bx + shift;
      ctx.save();
      ctx.translate(mx + 400, m.y);
      ctx.fillStyle = m._cachedGrad;
      ctx.beginPath();
      ctx.ellipse(0, 0, 450, m.h, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}

// --- FOREGROUND DRAWING HELPERS ---

function drawCanopyAndBranchesStatic(ctx) {
  ctx.save();

  const LEFT_LEAF_CLUSTERS = [
    { cx: 5,   cy: -25, rx: 145, ry: 80 },
    { cx: 95,  cy: 2,   rx: 125, ry: 70 },
    { cx: 180, cy: 16,  rx: 100, ry: 58 },
    { cx: 250, cy: 22,  rx: 75,  ry: 44 }
  ];
  for (const c of LEFT_LEAF_CLUSTERS) {
    const rad = ctx.createRadialGradient(c.cx, c.cy, c.rx * 0.2, c.cx, c.cy, c.rx);
    rad.addColorStop(0, '#1c3620');
    rad.addColorStop(0.65, '#122416');
    rad.addColorStop(1, '#0a140d');
    ctx.fillStyle = rad;
    ctx.beginPath();
    ctx.ellipse(c.cx, c.cy, c.rx, c.ry, 0.12, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.strokeStyle = 'rgba(40, 90, 60, 0.45)';
  ctx.lineWidth = 1.4;
  for (let mx = 75; mx <= 230; mx += 22) {
    const mLen = 14 + ((mx * 17) % 20);
    ctx.beginPath();
    ctx.moveTo(mx, 32 + ((mx * 7) % 10));
    ctx.lineTo(mx + ((mx % 2 === 0) ? -2 : 3), 32 + ((mx * 7) % 10) + mLen);
    ctx.stroke();
  }

  const RIGHT_LEAF_CLUSTERS = [
    { cx: WIDTH - 20,  cy: -25, rx: 160, ry: 85 },
    { cx: WIDTH - 145, cy: 5,   rx: 140, ry: 75 },
    { cx: WIDTH - 270, cy: 22,  rx: 130, ry: 65 },
    { cx: WIDTH - 390, cy: 30,  rx: 110, ry: 58 },
    { cx: WIDTH - 505, cy: 24,  rx: 90,  ry: 50 },
    { cx: WIDTH - 590, cy: 16,  rx: 75,  ry: 42 }
  ];
  for (const c of RIGHT_LEAF_CLUSTERS) {
    const rad = ctx.createRadialGradient(c.cx, c.cy, c.rx * 0.2, c.cx, c.cy, c.rx);
    rad.addColorStop(0, '#1e3a24');
    rad.addColorStop(0.65, '#132718');
    rad.addColorStop(1, '#0a140d');
    ctx.fillStyle = rad;
    ctx.beginPath();
    ctx.ellipse(c.cx, c.cy, c.rx, c.ry, -0.08, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const woodBase = '#182b1b';
  const woodShadow = '#0c160e';
  const woodHighlight = '#2d5236';

  ctx.strokeStyle = woodShadow;
  ctx.lineWidth = 18;
  ctx.beginPath();
  ctx.moveTo(-45, 22);
  ctx.quadraticCurveTo(45, 36, 105, 45);
  ctx.quadraticCurveTo(165, 54, 245, 38);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 15;
  ctx.beginPath();
  ctx.moveTo(-40, 20);
  ctx.quadraticCurveTo(45, 34, 105, 42);
  ctx.stroke();

  ctx.strokeStyle = woodHighlight;
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(-35, 15);
  ctx.quadraticCurveTo(45, 28, 105, 36);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 9.5;
  ctx.beginPath();
  ctx.moveTo(98, 42);
  ctx.bezierCurveTo(125, 48, 152, 62, 184, 68);
  ctx.quadraticCurveTo(205, 72, 228, 64);
  ctx.stroke();

  ctx.strokeStyle = woodHighlight;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(102, 38);
  ctx.bezierCurveTo(127, 44, 152, 58, 184, 64);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 4.2;
  ctx.beginPath();
  ctx.moveTo(158, 64);
  ctx.quadraticCurveTo(174, 82, 192, 92);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(212, 68);
  ctx.quadraticCurveTo(225, 78, 238, 76);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 8.5;
  ctx.beginPath();
  ctx.moveTo(105, 42);
  ctx.bezierCurveTo(138, 38, 172, 26, 206, 22);
  ctx.quadraticCurveTo(234, 18, 262, 24);
  ctx.stroke();

  ctx.strokeStyle = woodHighlight;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(110, 39);
  ctx.bezierCurveTo(140, 35, 172, 23, 206, 19);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 3.6;
  ctx.beginPath();
  ctx.moveTo(182, 24);
  ctx.quadraticCurveTo(196, 10, 214, 4);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 2.8;
  ctx.beginPath();
  ctx.moveTo(242, 20);
  ctx.quadraticCurveTo(256, 12, 272, 14);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 4.8;
  ctx.beginPath();
  ctx.moveTo(42, 34);
  ctx.quadraticCurveTo(58, 52, 76, 58);
  ctx.quadraticCurveTo(86, 62, 94, 59);
  ctx.stroke();

  // Right Canopy Bough
  ctx.strokeStyle = woodShadow;
  ctx.lineWidth = 20;
  ctx.beginPath();
  ctx.moveTo(WIDTH + 45, 12);
  ctx.quadraticCurveTo(WIDTH - 85, 26, WIDTH - 225, 34);
  ctx.quadraticCurveTo(WIDTH - 365, 42, WIDTH - 520, 28);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 16;
  ctx.beginPath();
  ctx.moveTo(WIDTH + 40, 10);
  ctx.quadraticCurveTo(WIDTH - 85, 24, WIDTH - 225, 31);
  ctx.quadraticCurveTo(WIDTH - 365, 38, WIDTH - 515, 25);
  ctx.stroke();

  ctx.strokeStyle = woodHighlight;
  ctx.lineWidth = 2.6;
  ctx.beginPath();
  ctx.moveTo(WIDTH + 35, 5);
  ctx.quadraticCurveTo(WIDTH - 85, 18, WIDTH - 225, 25);
  ctx.quadraticCurveTo(WIDTH - 365, 32, WIDTH - 515, 20);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 11;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 215, 32);
  ctx.bezierCurveTo(WIDTH - 275, 42, WIDTH - 335, 58, WIDTH - 395, 62);
  ctx.quadraticCurveTo(WIDTH - 435, 64, WIDTH - 475, 52);
  ctx.stroke();

  ctx.strokeStyle = woodHighlight;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 220, 28);
  ctx.bezierCurveTo(WIDTH - 278, 38, WIDTH - 336, 54, WIDTH - 395, 58);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 5.2;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 325, 56);
  ctx.quadraticCurveTo(WIDTH - 355, 78, WIDTH - 388, 88);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 3.6;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 425, 62);
  ctx.quadraticCurveTo(WIDTH - 452, 74, WIDTH - 478, 68);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 9.5;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 225, 31);
  ctx.bezierCurveTo(WIDTH - 305, 28, WIDTH - 385, 20, WIDTH - 465, 22);
  ctx.quadraticCurveTo(WIDTH - 535, 24, WIDTH - 595, 18);
  ctx.stroke();

  ctx.strokeStyle = woodHighlight;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 230, 27);
  ctx.bezierCurveTo(WIDTH - 308, 24, WIDTH - 386, 16, WIDTH - 465, 18);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 4.4;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 410, 20);
  ctx.quadraticCurveTo(WIDTH - 435, 8, WIDTH - 468, 2);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 525, 22);
  ctx.quadraticCurveTo(WIDTH - 558, 12, WIDTH - 588, 14);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 6.8;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 115, 26);
  ctx.bezierCurveTo(WIDTH - 145, 44, WIDTH - 175, 66, WIDTH - 205, 74);
  ctx.quadraticCurveTo(WIDTH - 222, 78, WIDTH - 238, 72);
  ctx.stroke();

  ctx.strokeStyle = woodBase;
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 175, 66);
  ctx.quadraticCurveTo(WIDTH - 192, 88, WIDTH - 210, 96);
  ctx.stroke();

  // Spun Spiderweb
  const wx2 = WIDTH - 218;
  const wy2 = 32;
  ctx.strokeStyle = 'rgba(215, 245, 235, 0.22)';
  ctx.lineWidth = 0.8;
  const webSpokes = 8;
  for (let s = 0; s < webSpokes; s++) {
    const angle = Math.PI - 1.4 + (1.2 / (webSpokes - 1)) * s;
    ctx.beginPath();
    ctx.moveTo(wx2, wy2);
    ctx.lineTo(wx2 + Math.cos(angle) * 44, wy2 + Math.sin(angle) * 44);
    ctx.stroke();
  }
  for (let r = 10; r <= 40; r += 10) {
    ctx.beginPath();
    ctx.arc(wx2, wy2, r, Math.PI - 1.4, Math.PI - 0.2);
    ctx.stroke();
  }

  ctx.restore();
}

function drawBarkScales(ctx) {
  ctx.save();
  const SCALE_ROWS = [
    { y: 70,  count: 3, startX: -65, span: 60, r: 12 },
    { y: 115, count: 3, startX: -55, span: 55, r: 13 },
    { y: 160, count: 4, startX: -60, span: 62, r: 14 },
    { y: 205, count: 4, startX: -52, span: 58, r: 13 },
    { y: 250, count: 4, startX: -50, span: 56, r: 14 },
    { y: 335, count: 4, startX: -45, span: 54, r: 14 },
    { y: 380, count: 4, startX: -40, span: 56, r: 15 },
    { y: 425, count: 5, startX: -32, span: 68, r: 16 }
  ];

  for (const row of SCALE_ROWS) {
    for (let i = 0; i < row.count; i++) {
      const sx = row.startX + (row.span / (row.count - 1 || 1)) * i;
      const sy = row.y + ((i % 2 === 0) ? -2 : 3);
      const sr = row.r;

      ctx.fillStyle = 'rgba(22, 42, 28, 0.88)';
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0.1, Math.PI - 0.1, false);
      ctx.quadraticCurveTo(sx, sy - sr * 0.45, sx + sr * Math.cos(0.1), sy + sr * Math.sin(0.1));
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = 'rgba(6, 12, 8, 0.9)';
      ctx.lineWidth = 1.8;
      ctx.stroke();

      ctx.strokeStyle = 'rgba(74, 222, 128, 0.28)';
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.arc(sx, sy, sr * 0.95, 0.2, Math.PI - 0.2, false);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawFramingTrunkLeftStatic(ctx) {
  ctx.save();

  ctx.beginPath();
  ctx.moveTo(-180, -40);
  ctx.lineTo(16, -40);
  ctx.bezierCurveTo(4, 100, 16, 220, 12, 330);
  ctx.bezierCurveTo(8, 410, 32, 480, 85, HEIGHT + 20);
  ctx.lineTo(-180, HEIGHT + 20);
  ctx.closePath();

  const barkGrad = ctx.createLinearGradient(-80, 0, 80, 0);
  barkGrad.addColorStop(0, '#09130c');
  barkGrad.addColorStop(0.45, '#122216');
  barkGrad.addColorStop(0.8, '#1e3423');
  barkGrad.addColorStop(1, '#2c4a32');
  ctx.fillStyle = barkGrad;
  ctx.fill();

  drawBarkScales(ctx);

  ctx.strokeStyle = 'rgba(8, 16, 11, 0.75)';
  ctx.lineWidth = 3.0;
  for (let ridgeX of [-90, -55, -20, 2]) {
    ctx.beginPath();
    ctx.moveTo(ridgeX, -20);
    ctx.bezierCurveTo(ridgeX - 8, 140, ridgeX + 6, 300, ridgeX + 18, HEIGHT + 10);
    ctx.stroke();
  }

  // Tree Hollow Cavity Base
  const hx = 6;
  const hy = 290;
  ctx.fillStyle = '#060d08';
  ctx.beginPath();
  ctx.ellipse(hx, hy, 16, 32, 0.15, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = 'rgba(74, 222, 128, 0.35)';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.ellipse(hx, hy, 16, 32, 0.15, Math.PI * 0.6, Math.PI * 1.8);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(74, 222, 128, 0.22)';
  ctx.lineWidth = 2.8;
  ctx.beginPath();
  ctx.moveTo(16, -20);
  ctx.bezierCurveTo(4, 100, 16, 220, 12, 330);
  ctx.bezierCurveTo(8, 410, 32, 480, 85, HEIGHT + 10);
  ctx.stroke();

  const ROOTS_LEFT = [
    { startX: 20, startY: 460, midX: 55, midY: 500, endX: 115, endY: 535, w: 20 },
    { startX: 6,  startY: 480, midX: 35, midY: 512, endX: 85,  endY: 545, w: 15 }
  ];

  for (const root of ROOTS_LEFT) {
    ctx.strokeStyle = '#182b1b';
    ctx.lineWidth = root.w;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(root.startX, root.startY);
    ctx.quadraticCurveTo(root.midX, root.midY, root.endX, root.endY);
    ctx.stroke();

    ctx.strokeStyle = '#27442d';
    ctx.lineWidth = root.w * 0.45;
    ctx.beginPath();
    ctx.moveTo(root.startX - 2, root.startY - 3);
    ctx.quadraticCurveTo(root.midX - 2, root.midY - 4, root.endX - 2, root.endY - 4);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(74, 222, 128, 0.25)';
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(root.startX - 3, root.startY - 5);
    ctx.quadraticCurveTo(root.midX - 3, root.midY - 6, root.endX - 3, root.endY - 6);
    ctx.stroke();
  }

  ctx.restore();
}

function drawFramingTrunkRightStatic(ctx) {
  ctx.save();

  ctx.beginPath();
  ctx.moveTo(WIDTH + 170, -40);
  ctx.lineTo(WIDTH - 15, -40);
  ctx.bezierCurveTo(WIDTH - 25, 110, WIDTH - 12, 230, WIDTH - 22, 335);
  ctx.bezierCurveTo(WIDTH - 30, 415, WIDTH - 48, 480, WIDTH - 90, HEIGHT + 20);
  ctx.lineTo(WIDTH + 170, HEIGHT + 20);
  ctx.closePath();

  const barkGrad = ctx.createLinearGradient(WIDTH + 80, 0, WIDTH - 80, 0);
  barkGrad.addColorStop(0, '#0a140d');
  barkGrad.addColorStop(0.45, '#152518');
  barkGrad.addColorStop(0.8, '#223a26');
  barkGrad.addColorStop(1, '#305036');
  ctx.fillStyle = barkGrad;
  ctx.fill();

  ctx.strokeStyle = 'rgba(8, 16, 11, 0.78)';
  ctx.lineWidth = 2.8;
  for (let ridgeX of [-95, -60, -25, -2]) {
    const rx = WIDTH - ridgeX;
    ctx.beginPath();
    ctx.moveTo(rx, -20);
    ctx.bezierCurveTo(rx + 12, 130, rx - 8, 290, rx - 24, HEIGHT + 10);
    ctx.stroke();
  }

  const KNOTS = [
    { y: 170, rx: 12, ry: 7 },
    { y: 320, rx: 14, ry: 8 },
    { y: 440, rx: 16, ry: 9 }
  ];
  for (const k of KNOTS) {
    const kx = WIDTH - 42;

    // The topmost knot doubles as the squirrel's peek-out hole (see
    // drawSwampSquirrel's 'peek' pose at the same (kx, 170)) - unlike the
    // two plain bark knots below it, it's meant to read as a leaf tucked
    // over the hole, not a bare ring, so it gets the same leaf-gradient
    // fill as ALL_CANOPY_LEAVES before the outline/vein strokes.
    if (k.y === 170) {
      const leafGrad = ctx.createLinearGradient(kx, k.y - k.ry, kx, k.y + k.ry);
      leafGrad.addColorStop(0, '#166534');
      leafGrad.addColorStop(0.5, '#22c55e');
      leafGrad.addColorStop(1, '#86efac');
      ctx.fillStyle = leafGrad;
      ctx.beginPath();
      ctx.ellipse(kx, k.y, k.rx, k.ry, -0.15, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.strokeStyle = 'rgba(7, 14, 9, 0.85)';
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.ellipse(kx, k.y, k.rx, k.ry, -0.15, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(kx, k.y, k.rx * 0.5, k.ry * 0.5, -0.15, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.strokeStyle = 'rgba(74, 222, 128, 0.22)';
  ctx.lineWidth = 2.8;
  ctx.beginPath();
  ctx.moveTo(WIDTH - 15, -20);
  ctx.bezierCurveTo(WIDTH - 25, 110, WIDTH - 12, 230, WIDTH - 22, 335);
  ctx.bezierCurveTo(WIDTH - 30, 415, WIDTH - 48, 480, WIDTH - 90, HEIGHT + 10);
  ctx.stroke();

  const ROOTS_RIGHT = [
    { startX: WIDTH - 28, startY: 465, midX: WIDTH - 70, midY: 505, endX: WIDTH - 138, endY: 535, w: 22 },
    { startX: WIDTH - 12, startY: 485, midX: WIDTH - 48, midY: 515, endX: WIDTH - 100, endY: 545, w: 16 },
    { startX: WIDTH + 8,  startY: 500, midX: WIDTH - 22, midY: 525, endX: WIDTH - 65,  endY: 550, w: 12 }
  ];

  for (const root of ROOTS_RIGHT) {
    ctx.strokeStyle = '#17271b';
    ctx.lineWidth = root.w;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(root.startX, root.startY);
    ctx.quadraticCurveTo(root.midX, root.midY, root.endX, root.endY);
    ctx.stroke();

    ctx.strokeStyle = '#27442d';
    ctx.lineWidth = root.w * 0.45;
    ctx.beginPath();
    ctx.moveTo(root.startX + 3, root.startY - 3);
    ctx.quadraticCurveTo(root.midX + 2, root.midY - 4, root.endX + 2, root.endY - 4);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(74, 222, 128, 0.26)';
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(root.startX + 4, root.startY - 5);
    ctx.quadraticCurveTo(root.midX + 3, root.midY - 6, root.endX + 3, root.endY - 6);
    ctx.stroke();
  }

  ctx.restore();
}

function drawForegroundCornerFloraStatic(ctx) {
  ctx.save();
  const STONES = [
    { x: 32, y: 528, rx: 18, ry: 9, col: '#16281a' },
    { x: 68, y: 532, rx: 14, ry: 7, col: '#1c3422' }
  ];
  for (const st of STONES) {
    ctx.fillStyle = st.col;
    ctx.beginPath();
    ctx.ellipse(st.x, st.y, st.rx, st.ry, 0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(74, 222, 128, 0.28)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.ellipse(st.x, st.y - 2, st.rx * 0.7, st.ry * 0.5, 0.1, Math.PI, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function drawTrunkBaseFades(ctx, bounds, height) {
  const h = height || 540;
  const visibleLeft = bounds ? bounds.visibleLeft : -40;
  const visibleRight = bounds ? bounds.visibleRight : 1640;
  const visibleBottom = bounds ? bounds.visibleBottom : h + 40;

  ctx.save();

  // 1. LEFT FLANK TRUNK BASE SHADOW FADE
  // Blends the left ancient banyan trunk base and roots seamlessly into the cockpit backdrop (#070b12)
  const leftX1 = Math.min(-60, visibleLeft);
  const leftPivotX = 65; // Center of trunk base on shelf
  const leftRadiusX = 95; // Horizontal spread towards the battlefield center
  const leftRadiusY = 65; // Vertical fade height (starts softly at y = h - 65 = 475)

  // Solid vertical fade column covering the left offscreen trunk core
  const vGradLeft = ctx.createLinearGradient(0, h - leftRadiusY, 0, h);
  vGradLeft.addColorStop(0, 'rgba(7, 11, 18, 0)');
  vGradLeft.addColorStop(0.35, 'rgba(7, 11, 18, 0.30)');
  vGradLeft.addColorStop(0.65, 'rgba(7, 11, 18, 0.72)');
  vGradLeft.addColorStop(0.88, 'rgba(7, 11, 18, 0.95)');
  vGradLeft.addColorStop(1, 'rgba(7, 11, 18, 1)');

  ctx.fillStyle = vGradLeft;
  ctx.fillRect(leftX1, h - leftRadiusY, leftPivotX - leftX1, leftRadiusY);

  // Elliptical radial fade feathering softly into the battlefield floor
  ctx.save();
  ctx.translate(leftPivotX, h);
  ctx.scale(1.0, leftRadiusY / leftRadiusX);
  const radGradLeft = ctx.createRadialGradient(0, 0, 0, 0, 0, leftRadiusX);
  radGradLeft.addColorStop(0, 'rgba(7, 11, 18, 1)');
  radGradLeft.addColorStop(0.35, 'rgba(7, 11, 18, 0.95)');
  radGradLeft.addColorStop(0.65, 'rgba(7, 11, 18, 0.72)');
  radGradLeft.addColorStop(0.88, 'rgba(7, 11, 18, 0.30)');
  radGradLeft.addColorStop(1, 'rgba(7, 11, 18, 0)');

  ctx.fillStyle = radGradLeft;
  ctx.beginPath();
  ctx.arc(0, 0, leftRadiusX, -Math.PI * 0.5, 0);
  ctx.lineTo(0, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // 2. RIGHT FLANK TRUNK BASE SHADOW FADE
  // Blends the right cypress trunk base and roots seamlessly into the cockpit backdrop (#070b12)
  const rightX2 = Math.max(WIDTH + 60, visibleRight);
  const rightPivotX = WIDTH - 65; // 1535
  const rightRadiusX = 95;
  const rightRadiusY = 65;

  // Solid vertical fade column covering the right offscreen trunk core
  const vGradRight = ctx.createLinearGradient(0, h - rightRadiusY, 0, h);
  vGradRight.addColorStop(0, 'rgba(7, 11, 18, 0)');
  vGradRight.addColorStop(0.35, 'rgba(7, 11, 18, 0.30)');
  vGradRight.addColorStop(0.65, 'rgba(7, 11, 18, 0.72)');
  vGradRight.addColorStop(0.88, 'rgba(7, 11, 18, 0.95)');
  vGradRight.addColorStop(1, 'rgba(7, 11, 18, 1)');

  ctx.fillStyle = vGradRight;
  ctx.fillRect(rightPivotX, h - rightRadiusY, rightX2 - rightPivotX, rightRadiusY);

  // Elliptical radial fade feathering softly into the battlefield floor
  ctx.save();
  ctx.translate(rightPivotX, h);
  ctx.scale(1.0, rightRadiusY / rightRadiusX);
  const radGradRight = ctx.createRadialGradient(0, 0, 0, 0, 0, rightRadiusX);
  radGradRight.addColorStop(0, 'rgba(7, 11, 18, 1)');
  radGradRight.addColorStop(0.35, 'rgba(7, 11, 18, 0.95)');
  radGradRight.addColorStop(0.65, 'rgba(7, 11, 18, 0.72)');
  radGradRight.addColorStop(0.88, 'rgba(7, 11, 18, 0.30)');
  radGradRight.addColorStop(1, 'rgba(7, 11, 18, 0)');

  ctx.fillStyle = radGradRight;
  ctx.beginPath();
  ctx.arc(0, 0, rightRadiusX, Math.PI, Math.PI * 1.5);
  ctx.lineTo(0, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // 3. LETTERBOX MARGIN OVERFLOW (if visibleBottom exceeds canvas height)
  if (visibleBottom > h) {
    ctx.fillStyle = 'rgb(7, 11, 18)';
    ctx.fillRect(leftX1, h, (leftPivotX + leftRadiusX) - leftX1, visibleBottom - h);
    ctx.fillRect(rightPivotX - rightRadiusX, h, rightX2 - (rightPivotX - rightRadiusX), visibleBottom - h);
  }

  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawTrunkShelfFungi() every
// single frame; its content never varies at runtime.
const SHELVES = Object.freeze([
  { y: 240, w: 38, d: 14, rot: -0.05, col: '#1e3e2c', glow: '#4ade80' },
  { y: 295, w: 46, d: 17, rot: 0.04,  col: '#244834', glow: '#6ee7b7' },
  { y: 355, w: 54, d: 20, rot: -0.08, col: '#1c3a28', glow: '#34d399' },
  { y: 415, w: 48, d: 18, rot: 0.06,  col: '#224430', glow: '#4ade80' }
]);

function drawTrunkShelfFungi(ctx, t, quality) {
  ctx.save();
  for (const s of SHELVES) {
    const tx = WIDTH - 22;
    const ty = s.y;
    const pulse = 0.85 + 0.15 * Math.sin(t * 2.0 + ty * 0.1);

    ctx.save();
    ctx.translate(tx, ty);
    ctx.rotate(s.rot);

    if (getQualityScale(quality) > 0) {
      if (!s._auraGrad) {
        s._auraGrad = ctx.createRadialGradient(-s.w * 0.5, s.d * 0.5, 0, -s.w * 0.5, s.d * 0.5, s.w * 1.3);
        s._auraGrad.addColorStop(0, 'rgba(52, 211, 153, 0.45)');
        s._auraGrad.addColorStop(0.6, 'rgba(52, 211, 153, 0.12)');
        s._auraGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      }
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = s._auraGrad;
      ctx.beginPath();
      ctx.arc(-s.w * 0.5, s.d * 0.5, s.w * 1.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.fillStyle = s.col;
    ctx.beginPath();
    ctx.ellipse(-s.w * 0.5, 0, s.w * 0.5, s.d * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();

    for (let r = 0.35; r <= 0.85; r += 0.25) {
      ctx.strokeStyle = 'rgba(74, 222, 128, 0.3)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.ellipse(-s.w * 0.5, 0, s.w * 0.5 * r, s.d * 0.5 * r, 0, Math.PI * 0.5, Math.PI * 1.5);
      ctx.stroke();
    }

    ctx.strokeStyle = s.glow;
    ctx.lineWidth = 2.0;
    ctx.beginPath();
    ctx.ellipse(-s.w * 0.5, 0, s.w * 0.5, s.d * 0.5, 0, Math.PI * 0.5, Math.PI * 1.5);
    ctx.stroke();

    ctx.fillStyle = `rgba(110, 231, 183, ${0.8 * pulse})`;
    ctx.beginPath();
    ctx.ellipse(-s.w * 0.5, s.d * 0.35, s.w * 0.45, s.d * 0.35, 0, 0, Math.PI);
    ctx.fill();

    ctx.restore();
  }
  ctx.restore();
}

let _treeHollowGlowGrad = null;
function getTreeHollowGlowGrad(ctx) {
  if (!_treeHollowGlowGrad) {
    _treeHollowGlowGrad = ctx.createRadialGradient(4, 294, 2, 6, 290, 26);
    _treeHollowGlowGrad.addColorStop(0, 'rgba(74, 222, 128, 0.45)');
    _treeHollowGlowGrad.addColorStop(0.5, 'rgba(34, 197, 94, 0.18)');
    _treeHollowGlowGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  }
  return _treeHollowGlowGrad;
}

function drawTreeHollowGlow(ctx, t) {
  const hx = 6;
  const hy = 290;
  const pulse = 0.85 + 0.15 * Math.sin(t * 1.8);
  ctx.save();
  ctx.globalAlpha = pulse;
  ctx.fillStyle = getTreeHollowGlowGrad(ctx);
  ctx.beginPath();
  ctx.ellipse(hx, hy, 26, 40, 0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawTrunkClimbingFern() every
// single frame; its content never varies at runtime.
const FERN_COLONIES = Object.freeze([
  { rx: 6,  ry: 215, scale: 0.85, frondCount: 5 },
  { rx: 12, ry: 310, scale: 1.05, frondCount: 6 },
  { rx: 18, ry: 395, scale: 1.15, frondCount: 7 }
]);

function drawTrunkClimbingFern(ctx, t) {
  ctx.save();
  for (const fc of FERN_COLONIES) {
    ctx.fillStyle = '#0f1c12';
    ctx.beginPath();
    ctx.ellipse(fc.rx - 8, fc.ry, 12 * fc.scale, 7 * fc.scale, 0, 0, Math.PI * 2);
    ctx.fill();

    for (let f = 0; f < fc.frondCount; f++) {
      const spreadAngle = -0.35 + (0.95 / (fc.frondCount - 1 || 1)) * f;
      const frondLen = (46 + Math.sin(f * 1.3) * 16) * fc.scale;
      const sway = Math.sin(t * 1.8 + fc.ry * 0.05 + f * 0.7) * (5 + f * 1.5);

      const tipX = fc.rx + Math.cos(spreadAngle) * frondLen + sway;
      const tipY = fc.ry + Math.sin(spreadAngle) * frondLen * 0.55 + (frondLen * 0.42);
      const midX = fc.rx + Math.cos(spreadAngle) * frondLen * 0.5 + sway * 0.4;
      const midY = fc.ry + Math.sin(spreadAngle) * frondLen * 0.28 + (frondLen * 0.15);

      ctx.strokeStyle = '#1e4b2e';
      ctx.lineWidth = 2.2 * fc.scale;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(fc.rx, fc.ry);
      ctx.quadraticCurveTo(midX, midY, tipX, tipY);
      ctx.stroke();

      const pinnaeCount = 6;
      for (let p = 1; p <= pinnaeCount; p++) {
        const prog = p / (pinnaeCount + 1);
        const px = fc.rx * (1 - prog) * (1 - prog) + 2 * (1 - prog) * prog * midX + prog * prog * tipX;
        const py = fc.ry * (1 - prog) * (1 - prog) + 2 * (1 - prog) * prog * midY + prog * prog * tipY;
        const pLen = (11 - p * 1.3) * fc.scale;

        for (let side of [-1, 1]) {
          const angle = spreadAngle + side * 1.1;
          ctx.strokeStyle = '#27613b';
          ctx.lineWidth = 1.4 * fc.scale;
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(px + Math.cos(angle) * pLen, py + Math.sin(angle) * pLen);
          ctx.stroke();

          ctx.strokeStyle = '#6ee7b7';
          ctx.lineWidth = 0.8 * fc.scale;
          ctx.beginPath();
          ctx.moveTo(px + Math.cos(angle) * pLen * 0.6, py + Math.sin(angle) * pLen * 0.6);
          ctx.lineTo(px + Math.cos(angle) * pLen, py + Math.sin(angle) * pLen);
          ctx.stroke();
        }
      }
    }
  }
  ctx.restore();
}

function drawHangingVines(ctx, t) {
  for (const v of HANGING_VINES) {
    const sway = Math.sin(t * (Math.PI * 2 / v.swayPeriod) + v.phase) * v.swayAmp;
    const tipX = v.startX + sway;
    const tipY = v.startY + v.length;
    const midX = v.startX + sway * 0.45;
    const midY = v.startY + v.length * 0.55;

    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = v.maxThick;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(v.startX, v.startY);
    ctx.quadraticCurveTo(midX, midY, tipX, tipY);
    ctx.stroke();

    ctx.strokeStyle = '#15803d';
    ctx.lineWidth = v.maxThick * 0.5;
    ctx.beginPath();
    ctx.moveTo(v.startX + 1, v.startY);
    ctx.quadraticCurveTo(midX + 1, midY, tipX + 1, tipY);
    ctx.stroke();

    const leafPairs = Math.floor(v.length / 18);
    for (let i = 1; i <= leafPairs; i++) {
      const p = i / (leafPairs + 1);
      const lx = v.startX * (1 - p) * (1 - p) + 2 * (1 - p) * p * midX + p * p * tipX;
      const ly = v.startY * (1 - p) * (1 - p) + 2 * (1 - p) * p * midY + p * p * tipY;

      for (let side of [-1, 1]) {
        ctx.save();
        ctx.translate(lx, ly);
        ctx.rotate(side * 0.75 + (sway / v.swayAmp) * 0.25);
        ctx.fillStyle = '#4ade80';
        ctx.beginPath();
        ctx.ellipse(side * 5, 0, 5, 2.4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    if (v.drip) {
      const dripPhase = (t * 0.75 + v.phase) % 2.5;
      if (dripPhase < 1.0) {
        const dropY = tipY + (dripPhase / 1.0) * 28;
        const dropA = 1.0 - dripPhase;
        ctx.fillStyle = `rgba(167, 243, 208, ${dropA * 0.8})`;
        ctx.beginPath();
        ctx.arc(tipX, dropY, 1.3, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
}

const LEFT_CANOPY_LEAVES = Object.freeze([
  { x: 28,  y: 18, rot: 1.15, len: 6.5,  phase: 0.3 },
  { x: 34,  y: 24, rot: 0.75, len: 9.0,  phase: 1.1 },
  { x: 52,  y: 14, rot: 1.35, len: 5.5,  phase: 2.8 },
  { x: 89,  y: 32, rot: 1.25, len: 10.5, phase: 0.6 },
  { x: 97,  y: 39, rot: 0.82, len: 8.0,  phase: 3.2 },
  { x: 104, y: 28, rot: 0.45, len: 6.0,  phase: 1.9 },
  { x: 122, y: 44, rot: 0.68, len: 11.0, phase: 4.1 },
  { x: 165, y: 38, rot: 0.88, len: 9.2,  phase: 0.2 },
  { x: 173, y: 33, rot: 0.35, len: 7.2,  phase: 2.4 },
  { x: 212, y: 34, rot: 0.52, len: 8.8,  phase: 1.5 },
  { x: 218, y: 26, rot: 0.15, len: 6.2,  phase: 3.7 },
  { x: 236, y: 28, rot: 0.38, len: 7.5,  phase: 0.9 },
  { x: 258, y: 20, rot: -0.22, len: 5.8, phase: 2.1 }
]);

const RIGHT_CANOPY_LEAVES = Object.freeze([
  { x: WIDTH - 588, y: 22, rot: -0.55, len: 6.5,  phase: 0.4 },
  { x: WIDTH - 578, y: 16, rot: -0.28, len: 9.0,  phase: 1.7 },
  { x: WIDTH - 534, y: 18, rot: -0.38, len: 6.0,  phase: 3.1 },
  { x: WIDTH - 492, y: 31, rot: -0.12, len: 10.5, phase: 0.8 },
  { x: WIDTH - 484, y: 37, rot: 0.18,  len: 8.5,  phase: 2.5 },
  { x: WIDTH - 475, y: 29, rot: -0.05, len: 6.8,  phase: 4.0 },
  { x: WIDTH - 448, y: 42, rot: 0.28,  len: 11.5, phase: 1.2 },
  { x: WIDTH - 402, y: 36, rot: 0.15,  len: 8.0,  phase: 3.5 },
  { x: WIDTH - 394, y: 40, rot: 0.42,  len: 9.8,  phase: 0.5 },
  { x: WIDTH - 332, y: 28, rot: 0.32,  len: 6.2,  phase: 2.2 },
  { x: WIDTH - 292, y: 34, rot: 0.48,  len: 10.0, phase: 1.0 },
  { x: WIDTH - 284, y: 39, rot: 0.65,  len: 8.2,  phase: 3.8 },
  { x: WIDTH - 275, y: 27, rot: 0.25,  len: 7.0,  phase: 0.2 },
  { x: WIDTH - 268, y: 36, rot: 0.58,  len: 9.2,  phase: 2.9 },
  { x: WIDTH - 212, y: 30, rot: 0.62,  len: 8.5,  phase: 1.6 },
  { x: WIDTH - 204, y: 22, rot: 0.45,  len: 6.5,  phase: 4.4 },
  { x: WIDTH - 165, y: 26, rot: 0.72,  len: 9.0,  phase: 2.0 },
  { x: WIDTH - 108, y: 16, rot: 0.85,  len: 8.0,  phase: 0.7 },
  { x: WIDTH - 98,  y: 22, rot: 1.05,  len: 6.5,  phase: 3.3 },
  { x: WIDTH - 72,  y: 12, rot: 0.90,  len: 7.5,  phase: 1.8 }
]);

const ALL_CANOPY_LEAVES = Object.freeze([...LEFT_CANOPY_LEAVES, ...RIGHT_CANOPY_LEAVES]);

function drawCanopyLeavesStatic(ctx) {
  ctx.save();
  for (const lf of ALL_CANOPY_LEAVES) {
    ctx.save();
    ctx.translate(lf.x, lf.y);
    ctx.rotate(lf.rot);

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(2.4, lf.len * 0.45, 0, lf.len);
    ctx.quadraticCurveTo(-2.4, lf.len * 0.45, 0, 0);
    ctx.closePath();

    const lfGrad = ctx.createLinearGradient(0, 0, 0, lf.len);
    lfGrad.addColorStop(0, '#166534');
    lfGrad.addColorStop(0.5, '#22c55e');
    lfGrad.addColorStop(1, '#86efac');
    ctx.fillStyle = lfGrad;
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(0, 0.5);
    ctx.lineTo(0, lf.len * 0.8);
    ctx.stroke();

    ctx.restore();
  }
  ctx.restore();
}

// Canopy-leaf fill gradient cache for drawCanopyLeavesAndFallingLeaves()
// below - its 3 color stops never vary, only the linear gradient's endpoint
// (0, lf.len) does, since ALL_CANOPY_LEAVES leaves aren't all the same
// length. Cached per distinct length (module-level lazy Map, same idiom as
// getWaterfallGradients() above) instead of building a fresh
// createLinearGradient() for each of the 33 leaves every single frame
// (Paradigm 3: LUTs for Gradients) - per-leaf rotation/flutter stays a live
// ctx.save()/translate()/rotate()/restore() below, unchanged.
let _canopyLeafGradCache = null;
function getCanopyLeafGradient(ctx, len) {
  if (!_canopyLeafGradCache) _canopyLeafGradCache = new Map();
  let grad = _canopyLeafGradCache.get(len);
  if (grad) return grad;
  grad = ctx.createLinearGradient(0, 0, 0, len);
  grad.addColorStop(0, '#166534');
  grad.addColorStop(0.5, '#22c55e');
  grad.addColorStop(1, '#86efac');
  _canopyLeafGradCache.set(len, grad);
  return grad;
}

function drawCanopyLeavesAndFallingLeaves(ctx, t, quality) {
  ctx.save();

  for (const lf of ALL_CANOPY_LEAVES) {
    const flutter = Math.sin(t * 2.8 + lf.phase) * 0.22 + Math.sin(t * 1.4 + lf.phase * 0.7) * 0.1;
    ctx.save();
    ctx.translate(lf.x, lf.y);
    ctx.rotate(lf.rot + flutter);

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(2.4, lf.len * 0.45, 0, lf.len);
    ctx.quadraticCurveTo(-2.4, lf.len * 0.45, 0, 0);
    ctx.closePath();

    ctx.fillStyle = getCanopyLeafGradient(ctx, lf.len);
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(0, 0.5);
    ctx.lineTo(0, lf.len * 0.8);
    ctx.stroke();

    ctx.restore();
  }

  const FALL_CYCLE = 22.0;
  const ft = t % FALL_CYCLE;
  const startX = WIDTH - 460;
  const startY = 38;
  const landX = WIDTH - 490;
  const landY = 460;

  if (ft < 14.5) {
    const p = ft / 14.5;
    const curY = startY + p * (landY - startY);
    const sway = Math.sin(ft * 2.4) * 34 + Math.sin(ft * 1.1) * 16;
    const curX = startX + (landX - startX) * p + sway;
    const rot = Math.sin(ft * 3.2) * 1.2 + ft * 0.7;
    const flipScale = Math.cos(ft * 4.2);

    ctx.save();
    ctx.translate(curX, curY);
    ctx.rotate(rot);
    ctx.scale(flipScale, 1.0);

    ctx.beginPath();
    ctx.moveTo(0, -6);
    ctx.quadraticCurveTo(3.2, 0, 0, 6);
    ctx.quadraticCurveTo(-3.2, 0, 0, -6);
    ctx.closePath();

    if (!_cachedFallingLeafGrad) {
      _cachedFallingLeafGrad = ctx.createLinearGradient(0, -6, 0, 6);
      _cachedFallingLeafGrad.addColorStop(0, '#4ade80');
      _cachedFallingLeafGrad.addColorStop(0.5, '#22c55e');
      _cachedFallingLeafGrad.addColorStop(1, '#a3e635');
    }
    ctx.fillStyle = _cachedFallingLeafGrad;
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(0, -5);
    ctx.lineTo(0, 5);
    ctx.stroke();
    ctx.restore();

    if (getQualityScale(quality) > 0) {
      for (let sp = 1; sp <= 2; sp++) {
        const spAlpha = Math.max(0, 0.7 - sp * 0.3);
        ctx.fillStyle = `rgba(110, 231, 183, ${spAlpha})`;
        ctx.beginPath();
        ctx.arc(curX - sway * 0.2, curY - sp * 7, 1.0, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (ft < 19.5) {
    const fadeAlpha = ft < 17.0 ? 0.9 : Math.max(0, (19.5 - ft) / 2.5 * 0.9);
    ctx.save();
    ctx.translate(landX, landY);
    ctx.rotate(0.35);
    ctx.globalAlpha = fadeAlpha;
    ctx.beginPath();
    ctx.ellipse(0, 0, 5.5, 2.2, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#22c55e';
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(-4, 0);
    ctx.lineTo(4, 0);
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawHeroMushroom() every single
// frame; its content never varies at runtime (local, pre-transform cap-spot
// offsets - unrelated to the function's own x/y/scale/facingRight params).
const SPOTS = Object.freeze([
  { x: -22, y: -78, r: 4.5 },
  { x: 5, y: -88, r: 6.0 },
  { x: 26, y: -74, r: 5.0 },
  { x: -38, y: -58, r: 3.8 },
  { x: -8, y: -65, r: 5.2 },
  { x: 18, y: -58, r: 4.2 },
  { x: 38, y: -52, r: 3.2 }
]);

// Hero Mushroom bioluminescent aura (docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md
// §4) - always the same local-space gradient (origin/radii fixed after the
// translate+scale below); only `pulse` varies per frame, and every stop
// shares that same multiplier, so baking once at pulse=1 and applying it via
// ctx.globalAlpha at draw time is exact, not an approximation.
let _heroMushroomAuraGrad = null;
function getHeroMushroomAuraGrad(ctx) {
  if (!_heroMushroomAuraGrad) {
    const g = ctx.createRadialGradient(0, -35, 10, 0, -35, 110);
    g.addColorStop(0, 'rgba(163, 230, 53, 0.32)');
    g.addColorStop(0.45, 'rgba(132, 204, 22, 0.14)');
    g.addColorStop(0.85, 'rgba(77, 124, 15, 0.03)');
    g.addColorStop(1, 'rgba(0, 0, 0, 0)');
    _heroMushroomAuraGrad = g;
  }
  return _heroMushroomAuraGrad;
}

function drawHeroMushroom(ctx, x, y, scale, facingRight, t, quality) {
  const pulse = 0.9 + 0.1 * Math.sin(t * 1.8 + x);
  const dir = facingRight ? 1 : -1;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale * dir, scale);

  if (getQualityScale(quality) > 0) {
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.fillStyle = getHeroMushroomAuraGrad(ctx);
    ctx.beginPath();
    ctx.arc(0, -35, 110, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  ctx.beginPath();
  ctx.moveTo(-16, 10);
  ctx.quadraticCurveTo(-14, -25, -20, -45);
  ctx.lineTo(20, -45);
  ctx.quadraticCurveTo(14, -25, 22, 10);
  ctx.closePath();

  if (!_cachedHeroStemGrad) {
    _cachedHeroStemGrad = ctx.createLinearGradient(-15, 0, 20, 0);
    _cachedHeroStemGrad.addColorStop(0, '#365314');
    _cachedHeroStemGrad.addColorStop(0.4, '#4d7c0f');
    _cachedHeroStemGrad.addColorStop(0.75, '#65a30d');
    _cachedHeroStemGrad.addColorStop(1, '#3f6212');
  }
  ctx.fillStyle = _cachedHeroStemGrad;
  ctx.fill();

  ctx.strokeStyle = '#273e0e';
  ctx.lineWidth = 2.0;
  ctx.beginPath();
  ctx.moveTo(-6, 8);
  ctx.quadraticCurveTo(-4, -20, -6, -42);
  ctx.moveTo(8, 8);
  ctx.quadraticCurveTo(6, -20, 8, -42);
  ctx.stroke();

  ctx.fillStyle = '#1e330a';
  ctx.beginPath();
  ctx.ellipse(0, -42, 54, 16, 0, 0, Math.PI);
  ctx.fill();

  ctx.strokeStyle = '#365314';
  ctx.lineWidth = 1.4;
  for (let gx = -46; gx <= 46; gx += 7) {
    ctx.beginPath();
    ctx.moveTo(0, -42);
    ctx.lineTo(gx, -42 + Math.sqrt(Math.max(0, 1 - (gx * gx) / (54 * 54))) * 15);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.moveTo(-56, -42);
  ctx.bezierCurveTo(-58, -85, -28, -100, 0, -100);
  ctx.bezierCurveTo(28, -100, 58, -85, 56, -42);
  ctx.quadraticCurveTo(0, -32, -56, -42);
  ctx.closePath();

  if (!_cachedHeroCapGrad) {
    _cachedHeroCapGrad = ctx.createRadialGradient(0, -75, 10, 0, -60, 65);
    _cachedHeroCapGrad.addColorStop(0, '#ecfccb');
    _cachedHeroCapGrad.addColorStop(0.3, '#bef264');
    _cachedHeroCapGrad.addColorStop(0.65, '#84cc16');
    _cachedHeroCapGrad.addColorStop(0.9, '#4d7c0f');
    _cachedHeroCapGrad.addColorStop(1, '#365314');
  }
  ctx.fillStyle = _cachedHeroCapGrad;
  ctx.fill();

  ctx.fillStyle = 'rgba(254, 252, 232, 0.85)';
  for (const s of SPOTS) {
    ctx.beginPath();
    ctx.ellipse(s.x, s.y, s.r, s.r * 0.75, 0.2, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.strokeStyle = '#d9f99d';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(-56, -42);
  ctx.bezierCurveTo(-58, -85, -28, -100, 0, -100);
  ctx.bezierCurveTo(28, -100, 58, -85, 56, -42);
  ctx.stroke();

  ctx.restore();
}

function drawFiddlehead(ctx, x, y, scale, t) {
  const sway = Math.sin(t * 1.6 + x * 0.1) * 3;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  ctx.strokeStyle = '#1b4d2e';
  ctx.lineWidth = 3.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(8 + sway * 0.5, -22, 6 + sway, -42);
  ctx.stroke();

  ctx.strokeStyle = '#4ade80';
  ctx.lineWidth = 1.6;
  ctx.stroke();

  ctx.save();
  ctx.translate(6 + sway, -42);
  ctx.strokeStyle = '#86efac';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  let r = 8;
  for (let a = 0; a < Math.PI * 3.5; a += 0.25) {
    r *= 0.94;
    const ax = Math.cos(a) * r;
    const ay = Math.sin(a) * r;
    if (a === 0) ctx.moveTo(ax, ay);
    else ctx.lineTo(ax, ay);
  }
  ctx.stroke();
  ctx.restore();

  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawAlienCrystals() every single
// frame; its content never varies at runtime (local, pre-transform shard
// geometry - unrelated to the function's own x/y/scale/color params).
const SHARDS = Object.freeze([
  { tipX: 0, tipY: -50, baseL: -9, baseR: 9, h: 50 },
  { tipX: -14, tipY: -35, baseL: -20, baseR: -8, h: 35 },
  { tipX: 12, tipY: -38, baseL: 7, baseR: 19, h: 38 }
]);

// Alien Crystal aura, keyed by color variant - same "bake at pulse=1, apply
// via ctx.globalAlpha" reasoning as getHeroMushroomAuraGrad() above (single
// non-zero colorstop, so the scalar multiply is exact).
const _alienCrystalAuraGrads = new Map();
function getAlienCrystalAuraGrad(ctx, isPink) {
  let g = _alienCrystalAuraGrads.get(isPink);
  if (!g) {
    g = ctx.createRadialGradient(0, -25, 5, 0, -25, 45);
    g.addColorStop(0, isPink ? 'rgba(236, 72, 153, 0.45)' : 'rgba(6, 182, 212, 0.45)');
    g.addColorStop(1, 'rgba(0, 0, 0, 0)');
    _alienCrystalAuraGrads.set(isPink, g);
  }
  return g;
}

function drawAlienCrystals(ctx, x, y, scale, color, t, quality) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  const pulse = 0.88 + 0.12 * Math.sin(t * 2.2 + x);
  const isPink = color === 'magenta';
  const cMain = isPink ? '#ec4899' : '#06b6d4';
  const cLight = isPink ? '#fbcfe8' : '#cffafe';
  const cDark = isPink ? '#9d174d' : '#0e7490';

  if (getQualityScale(quality) > 0) {
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.fillStyle = getAlienCrystalAuraGrad(ctx, isPink);
    ctx.beginPath();
    ctx.arc(0, -25, 45, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  for (const s of SHARDS) {
    ctx.fillStyle = cDark;
    ctx.beginPath();
    ctx.moveTo(s.tipX, s.tipY);
    ctx.lineTo(s.baseL, 0);
    ctx.lineTo((s.baseL + s.baseR) * 0.5, 0);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = cMain;
    ctx.beginPath();
    ctx.moveTo(s.tipX, s.tipY);
    ctx.lineTo((s.baseL + s.baseR) * 0.5, 0);
    ctx.lineTo(s.baseR, 0);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = cLight;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(s.tipX, s.tipY);
    ctx.lineTo((s.baseL + s.baseR) * 0.5, 0);
    ctx.stroke();
  }

  ctx.restore();
}

function drawForegroundCornerFloraDynamic(ctx, t, quality) {
  ctx.save();
  for (let i = 0; i < 3; i++) {
    const fx = 48 + i * 22;
    const fy = 532;
    const fSway = Math.sin(t * 1.8 + i) * 2;
    ctx.strokeStyle = '#2d6a4f';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(fx, fy);
    ctx.quadraticCurveTo(fx - 6 + fSway * 0.5, fy - 14, fx - 10 + fSway, fy - 26);
    ctx.stroke();
    ctx.strokeStyle = '#74c69d';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(fx - 6 + fSway * 0.5, fy - 14);
    ctx.lineTo(fx - 10 + fSway, fy - 26);
    ctx.stroke();
  }
  ctx.restore();

  drawFiddlehead(ctx, WIDTH - 130, 532, 0.85, t);
  drawFiddlehead(ctx, WIDTH - 100, 535, 0.65, t);
  drawAlienCrystals(ctx, WIDTH - 110, 528, 0.72, 'cyan', t, quality);
  drawHeroMushroom(ctx, WIDTH - 50, 516, 0.78, false, t, quality);
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawAnimatedFern() every single
// frame (as `fronds`, renamed to match this file's ALL_CAPS convention for
// hoisted per-function configs); its content never varies at runtime (local,
// pre-transform frond geometry - unrelated to the function's x/y/scale).
const FERN_FRONDS = Object.freeze([
  { angle: -1.05, len: 48, rate: 1.8, phase: 0.2 },
  { angle: -0.70, len: 62, rate: 1.5, phase: 1.1 },
  { angle: -0.35, len: 74, rate: 1.3, phase: 2.3 },
  { angle: 0.05,  len: 66, rate: 1.6, phase: 0.7 },
  { angle: 0.40,  len: 52, rate: 1.9, phase: 1.8 }
]);

function drawAnimatedFern(ctx, x, y, scale, t) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  for (let i = 0; i < FERN_FRONDS.length; i++) {
    const f = FERN_FRONDS[i];
    const sway = Math.sin(t * f.rate + f.phase) * 6;
    const tipX = Math.cos(f.angle) * f.len + sway;
    const tipY = Math.sin(f.angle) * f.len + Math.abs(sway) * 0.3;
    const midX = Math.cos(f.angle) * f.len * 0.5 + sway * 0.4;
    const midY = Math.sin(f.angle) * f.len * 0.5;

    ctx.strokeStyle = '#1b4332';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(midX, midY, tipX, tipY);
    ctx.stroke();

    const pinnae = 6;
    for (let p = 1; p <= pinnae; p++) {
      const prog = p / (pinnae + 1);
      const px = 2 * (1 - prog) * prog * midX + prog * prog * tipX;
      const py = 2 * (1 - prog) * prog * midY + prog * prog * tipY;
      const pLen = (12 - p * 1.3);

      for (let side of [-1, 1]) {
        const pinnaAngle = f.angle + side * 1.15;
        ctx.strokeStyle = '#2d6a4f';
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px + Math.cos(pinnaAngle) * pLen, py + Math.sin(pinnaAngle) * pLen);
        ctx.stroke();

        ctx.strokeStyle = '#74c69d';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(px + Math.cos(pinnaAngle) * pLen * 0.6, py + Math.sin(pinnaAngle) * pLen * 0.6);
        ctx.lineTo(px + Math.cos(pinnaAngle) * pLen, py + Math.sin(pinnaAngle) * pLen);
        ctx.stroke();
      }
    }
  }

  ctx.restore();
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawAnimatedSporeBulrushes() every
// single frame; its content never varies at runtime (local, pre-transform
// stem geometry - unrelated to the function's own x/y/scale params).
const STEMS = Object.freeze([
  { offX: -14, h: 58, rate: 1.7, phase: 0.3, glow: '#4ade80' },
  { offX: -4,  h: 74, rate: 1.4, phase: 1.6, glow: '#6ee7b7' },
  { offX: 8,   h: 64, rate: 1.8, phase: 2.7, glow: '#34d399' },
  { offX: 18,  h: 52, rate: 2.0, phase: 0.9, glow: '#4ade80' }
]);

function drawAnimatedSporeBulrushes(ctx, x, y, scale, t, quality) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  for (const s of STEMS) {
    const sway = Math.sin(t * s.rate + s.phase) * 5;
    const tipX = s.offX + sway;
    const tipY = -s.h;
    const midX = s.offX + sway * 0.45;
    const midY = -s.h * 0.55;

    ctx.strokeStyle = '#1b3824';
    ctx.lineWidth = 2.0;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(s.offX, 0);
    ctx.quadraticCurveTo(midX, midY, tipX, tipY);
    ctx.stroke();

    const pulse = 0.85 + 0.15 * Math.sin(t * 2.4 + s.phase);

    if (getQualityScale(quality) > 0) {
      if (!_cachedBulrushAuraGrad) {
        _cachedBulrushAuraGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 14);
        _cachedBulrushAuraGrad.addColorStop(0, 'rgba(74, 222, 128, 0.45)');
        _cachedBulrushAuraGrad.addColorStop(1, 'rgba(74, 222, 128, 0)');
      }
      ctx.save();
      ctx.translate(tipX, tipY);
      ctx.globalAlpha = pulse;
      ctx.fillStyle = _cachedBulrushAuraGrad;
      ctx.beginPath();
      ctx.arc(0, 0, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.fillStyle = '#1e3e2c';
    ctx.beginPath();
    ctx.ellipse(tipX, tipY, 4, 10, sway * 0.05, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = s.glow;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.ellipse(tipX, tipY, 4, 10, sway * 0.05, 0, Math.PI * 2);
    ctx.stroke();

    if (getQualityScale(quality) > 0.5) {
      const sporeProgress = (t * 0.6 + s.phase) % 2.5;
      if (sporeProgress < 1.4) {
        const spY = tipY - (sporeProgress / 1.4) * 22;
        const spX = tipX + Math.sin(t * 2.0 + s.phase) * 6;
        const spA = (1.0 - sporeProgress / 1.4) * 0.8;
        ctx.fillStyle = `rgba(167, 243, 208, ${spA})`;
        ctx.beginPath();
        ctx.arc(spX, spY, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  ctx.restore();
}

function drawForegroundGrass(ctx, t, quality) {
  const isBakedLow = quality === 'low_baked';
  if (!isBakedLow && getQualityScale(quality) === 0) return;

  for (const tuft of FOREGROUND_GRASS) {
    const sway = t === 0 ? 0 : Math.sin(t * tuft.rate + tuft.phase) * tuft.amp;

    for (let i = 0; i < tuft.bladeCount; i++) {
      const spread = (i - (tuft.bladeCount - 1) * 0.5) * 4;
      const bladeH = tuft.maxH * (0.65 + 0.35 * Math.sin(((i + 1) / (tuft.bladeCount + 1)) * Math.PI));
      const bladeSway = sway * (0.7 + 0.3 * (i / tuft.bladeCount));

      const bx = tuft.x + spread;
      const by = tuft.y;
      const tipX = tuft.x + spread * 1.3 + bladeSway;
      const tipY = tuft.y - bladeH;
      const midX = tuft.x + spread * 0.6 + bladeSway * 0.4;
      const midY = tuft.y - bladeH * 0.55;

      ctx.strokeStyle = tuft.col;
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.quadraticCurveTo(midX, midY, tipX, tipY);
      ctx.stroke();

      ctx.strokeStyle = tuft.tipCol;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(midX, midY);
      ctx.lineTo(tipX, tipY);
      ctx.stroke();
    }
  }
}

// --- CRITTERS: THE SWAMP SQUIRREL ---

let _squirrelHGrad = null;
let _squirrelTailBaseGrad = null;
let _squirrelBGrad = null;
let _squirrelTailUpGrad = null;
let _squirrelTailDownGrad = null;
let _squirrelRunBGrad = null;

function drawSwampSquirrel(ctx, t, quality) {
  const isPerchStatic = quality === 'perch_static';
  if (!isPerchStatic && getQualityScale(quality) === 0) return;

  const SQUIRREL_CYCLE = 24.0;
  const st = (t + 5.0) % SQUIRREL_CYCLE;

  let x = WIDTH - 42;
  let y = 170;
  let rot = 0;
  let pose = 'peek';
  let berry = false;
  let legPhase = 0;
  let tailSway = 0;

  if (st < 3.5) {
    pose = 'peek';
    x = WIDTH - 42;
    y = 170;
    rot = -0.15;
  } else if (st < 7.0) {
    pose = 'run_down';
    const p = (st - 3.5) / 3.5;
    const pSmooth = p * p * (3 - 2 * p);
    y = 170 + pSmooth * 180;
    x = (WIDTH - 42) + Math.sin(pSmooth * Math.PI) * 12;
    rot = 0.32;
    legPhase = Math.sin(t * 26);
    tailSway = Math.sin(t * 15) * 0.35;
  } else if (st < 12.5) {
    pose = 'perch';
    x = WIDTH - 44;
    y = 348;
    rot = -0.12;
    berry = (st >= 8.5 && st <= 11.2);
    tailSway = Math.sin(t * 3.8) * 0.22;
  } else if (st < 15.0) {
    pose = 'run_down';
    const p = (st - 12.5) / 2.5;
    const pSmooth = p * p * (3 - 2 * p);
    y = 348 + pSmooth * 87;
    x = (WIDTH - 44) - pSmooth * 16;
    rot = 0.42;
    legPhase = Math.sin(t * 26);
    tailSway = Math.sin(t * 15) * 0.35;
  } else if (st < 16.5) {
    pose = 'sniff';
    x = WIDTH - 60;
    y = 435;
    rot = 0.2;
    tailSway = Math.sin(t * 5.0) * 0.28;
  } else if (st < 20.0) {
    pose = 'run_up';
    const p = (st - 16.5) / 3.5;
    const pSmooth = p * p * (3 - 2 * p);
    y = 435 - pSmooth * 265;
    x = (WIDTH - 60) + pSmooth * 18;
    rot = -0.35;
    legPhase = Math.sin(t * 28);
    tailSway = Math.sin(t * 16) * 0.35;
  } else {
    return;
  }

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);

  if (pose === 'peek') {
    const earTwitch = Math.sin(t * 12) * 0.15;

    if (!_squirrelHGrad) {
      _squirrelHGrad = ctx.createRadialGradient(-2, -2, 1, 0, 0, 7);
      _squirrelHGrad.addColorStop(0, '#ea580c');
      _squirrelHGrad.addColorStop(0.7, '#c2410c');
      _squirrelHGrad.addColorStop(1, '#9a3412');
    }
    ctx.fillStyle = _squirrelHGrad;
    ctx.beginPath();
    ctx.arc(-2, 0, 5.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#b45309';
    ctx.beginPath();
    ctx.moveTo(-5, -4);
    ctx.lineTo(-7 + earTwitch, -11);
    ctx.lineTo(-3, -5);
    ctx.closePath();
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(-1, -4);
    ctx.lineTo(0 - earTwitch, -11);
    ctx.lineTo(2, -5);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#34d399';
    ctx.beginPath();
    ctx.arc(-7 + earTwitch, -10.5, 1.2, 0, Math.PI * 2);
    ctx.arc(0 - earTwitch, -10.5, 1.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#090d0b';
    ctx.beginPath();
    ctx.arc(-4, -1, 1.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-4.4, -1.4, 0.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fed7aa';
    ctx.beginPath();
    ctx.arc(-7, 1, 2.0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(-8, 1, 0.8, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#9a3412';
    ctx.beginPath();
    ctx.arc(-5, 4, 1.5, 0, Math.PI * 2);
    ctx.arc(-1, 4.5, 1.5, 0, Math.PI * 2);
    ctx.fill();

  } else if (pose === 'perch') {
    const earTwitch = Math.sin(t * 10) * 0.12;
    const nibble = berry ? Math.sin(t * 22) * 1.0 : 0;

    ctx.save();
    ctx.translate(6, 2);
    if (!_squirrelTailBaseGrad) {
      _squirrelTailBaseGrad = ctx.createLinearGradient(0, 0, 4, -18);
      _squirrelTailBaseGrad.addColorStop(0, '#b45309');
      _squirrelTailBaseGrad.addColorStop(0.5, '#ea580c');
      _squirrelTailBaseGrad.addColorStop(0.85, '#f59e0b');
      _squirrelTailBaseGrad.addColorStop(1, '#34d399');
    }
    ctx.fillStyle = _squirrelTailBaseGrad;

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(8, -6, 7 + tailSway * 8, -16);
    ctx.quadraticCurveTo(5 + tailSway * 8, -22, 0 + tailSway * 4, -22);
    ctx.quadraticCurveTo(-4, -18, -2, -10);
    ctx.quadraticCurveTo(0, -4, 0, 0);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = 'rgba(254, 215, 170, 0.45)';
    ctx.lineWidth = 1.0;
    ctx.stroke();
    ctx.restore();

    ctx.fillStyle = '#9a3412';
    ctx.beginPath();
    ctx.ellipse(3, 4, 4.5, 3.5, 0.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#1c1917';
    ctx.beginPath();
    ctx.arc(3, 7, 1.2, 0, Math.PI * 2);
    ctx.fill();

    if (!_squirrelBGrad) {
      _squirrelBGrad = ctx.createLinearGradient(4, 3, -4, -4);
      _squirrelBGrad.addColorStop(0, '#9a3412');
      _squirrelBGrad.addColorStop(0.5, '#c2410c');
      _squirrelBGrad.addColorStop(1, '#ea580c');
    }
    ctx.fillStyle = _squirrelBGrad;
    ctx.beginPath();
    ctx.ellipse(1, 0, 5.0, 7.0, -0.25, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fed7aa';
    ctx.beginPath();
    ctx.ellipse(-1.5, 0, 2.5, 5.0, -0.25, 0, Math.PI * 2);
    ctx.fill();

    ctx.save();
    ctx.translate(-3, -7);
    ctx.rotate(nibble * 0.08);

    ctx.fillStyle = '#c2410c';
    ctx.beginPath();
    ctx.arc(0, 0, 4.8, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#b45309';
    ctx.beginPath();
    ctx.moveTo(-1, -3);
    ctx.lineTo(-3 + earTwitch, -10);
    ctx.lineTo(1, -4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#34d399';
    ctx.beginPath();
    ctx.arc(-3 + earTwitch, -9.5, 1.1, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fed7aa';
    ctx.beginPath();
    ctx.ellipse(-3.5, 1.5, 2.4, 1.8, 0.1, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(-5.2, 1.5, 0.8, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#090d0b';
    ctx.beginPath();
    ctx.arc(-1.5, -0.8, 1.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-1.8, -1.2, 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = '#b45309';
    ctx.beginPath();
    ctx.arc(-4, -1 + nibble, 1.6, 0, Math.PI * 2);
    ctx.fill();

    if (berry) {
      ctx.fillStyle = '#67e8f9';
      ctx.beginPath();
      ctx.arc(-6, -2 + nibble, 2.0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(-6.6, -2.6 + nibble, 0.6, 0, Math.PI * 2);
      ctx.fill();
    }

  } else {
    const isUp = (pose === 'run_up');

    ctx.save();
    ctx.translate(0, isUp ? 8 : -8);
    if (isUp) {
      if (!_squirrelTailUpGrad) {
        _squirrelTailUpGrad = ctx.createLinearGradient(0, 0, 0, 16);
        _squirrelTailUpGrad.addColorStop(0, '#9a3412');
        _squirrelTailUpGrad.addColorStop(0.6, '#ea580c');
        _squirrelTailUpGrad.addColorStop(1, '#34d399');
      }
      ctx.fillStyle = _squirrelTailUpGrad;
    } else {
      if (!_squirrelTailDownGrad) {
        _squirrelTailDownGrad = ctx.createLinearGradient(0, 0, 0, -16);
        _squirrelTailDownGrad.addColorStop(0, '#9a3412');
        _squirrelTailDownGrad.addColorStop(0.6, '#ea580c');
        _squirrelTailDownGrad.addColorStop(1, '#34d399');
      }
      ctx.fillStyle = _squirrelTailDownGrad;
    }

    ctx.beginPath();
    ctx.moveTo(-2, 0);
    ctx.quadraticCurveTo(tailSway * 8, isUp ? 10 : -10, tailSway * 12, isUp ? 18 : -18);
    ctx.quadraticCurveTo(tailSway * 4, isUp ? 14 : -14, 2, 0);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = '#9a3412';
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-3, -3);
    ctx.lineTo(-6 - legPhase * 3, -6);
    ctx.moveTo(3, -3);
    ctx.lineTo(6 + legPhase * 3, -6);
    ctx.moveTo(-3, 3);
    ctx.lineTo(-6 + legPhase * 3, 6);
    ctx.moveTo(3, 3);
    ctx.lineTo(6 - legPhase * 3, 6);
    ctx.stroke();

    if (!_squirrelRunBGrad) {
      _squirrelRunBGrad = ctx.createLinearGradient(0, -6, 0, 6);
      _squirrelRunBGrad.addColorStop(0, '#9a3412');
      _squirrelRunBGrad.addColorStop(0.5, '#c2410c');
      _squirrelRunBGrad.addColorStop(1, '#ea580c');
    }
    ctx.fillStyle = _squirrelRunBGrad;
    ctx.beginPath();
    ctx.ellipse(0, 0, 4.2, 7.5, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.save();
    ctx.translate(0, isUp ? -8 : 8);

    ctx.fillStyle = '#c2410c';
    ctx.beginPath();
    ctx.arc(0, 0, 4.2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#fed7aa';
    ctx.beginPath();
    ctx.arc(0, isUp ? -3 : 3, 1.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(0, isUp ? -4.2 : 4.2, 0.7, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#b45309';
    ctx.beginPath();
    ctx.moveTo(-2, isUp ? 1 : -1);
    ctx.lineTo(-4, isUp ? 6 : -6);
    ctx.lineTo(-1, isUp ? 3 : -3);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#34d399';
    ctx.beginPath();
    ctx.arc(-4, isUp ? 5.5 : -5.5, 0.9, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#090d0b';
    ctx.beginPath();
    ctx.arc(-1.5, 0, 1.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-1.8, -0.3, 0.4, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  ctx.restore();
}

// --- WEATHER / AMBIENT ATMOSPHERIC LAYERS ---

let _sporeAuraGrad = null;
function getSporeAuraGrad(ctx) {
  if (!_sporeAuraGrad) {
    _sporeAuraGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    _sporeAuraGrad.addColorStop(0, 'rgba(190, 242, 100, 0.45)');
    _sporeAuraGrad.addColorStop(1, 'rgba(190, 242, 100, 0)');
  }
  return _sporeAuraGrad;
}

function drawFloatingSpores(ctx, t, quality) {
  if (getQualityScale(quality) === 0) return;

  const auraGrad = getSporeAuraGrad(ctx);
  for (const s of SPORES) {
    const ySpan = LANE_H + 80;
    const progress = ((t * -s.speedY + s.phase * 40) % ySpan + ySpan) % ySpan;
    const currentY = LANE_BOTTOM + 20 - progress;
    const drift = Math.sin(t * s.driftFreq + s.phase) * s.driftAmp;
    const currentX = s.x + drift;
    const pulse = 0.75 + 0.25 * Math.sin(t * 3.0 + s.phase * 2);

    const rad = s.r * 3.2;
    ctx.save();
    ctx.translate(currentX, currentY);
    ctx.scale(rad, rad);
    ctx.globalAlpha *= pulse;
    ctx.fillStyle = auraGrad;
    ctx.beginPath();
    ctx.arc(0, 0, 1, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = s.hue;
    ctx.beginPath();
    ctx.arc(currentX, currentY, s.r * 0.7, 0, Math.PI * 2);
    ctx.fill();
  }
}

// Hoisted to module scope and frozen (Paradigm 1: Zero-Allocation) - was a
// fresh literal array re-allocated inside drawSweepingSmokeFog() every
// single frame; its content never varies at runtime.
const FG_SMOKE_PUFFS = Object.freeze([
  { dx: 0,    dy: 508, rx: 140, ry: 38, col: 'rgba(18, 42, 32, ', alpha: 0.32 },
  { dx: 190,  dy: 522, rx: 170, ry: 44, col: 'rgba(12, 28, 20, ', alpha: 0.38 },
  { dx: 390,  dy: 498, rx: 150, ry: 36, col: 'rgba(24, 58, 44, ', alpha: 0.28 },
  { dx: 580,  dy: 516, rx: 190, ry: 46, col: 'rgba(15, 36, 28, ', alpha: 0.36 },
  { dx: 800,  dy: 504, rx: 160, ry: 40, col: 'rgba(20, 48, 36, ', alpha: 0.30 },
  { dx: 1000, dy: 526, rx: 180, ry: 45, col: 'rgba(13, 30, 24, ', alpha: 0.38 },
  { dx: 1220, dy: 510, rx: 165, ry: 38, col: 'rgba(22, 54, 42, ', alpha: 0.32 },
  { dx: 1440, dy: 520, rx: 185, ry: 44, col: 'rgba(14, 34, 26, ', alpha: 0.35 }
]);

const _fgSmokeGrads = [];
const _fgFringeGrads = [];
function getFgSmokeGrad(ctx, i, p) {
  if (!_fgSmokeGrads[i]) {
    const grad = ctx.createRadialGradient(0, 0, 15, 0, 0, p.rx);
    grad.addColorStop(0, `${p.col}${p.alpha})`);
    grad.addColorStop(0.45, `${p.col}${p.alpha * 0.65})`);
    grad.addColorStop(0.8, `${p.col}${p.alpha * 0.2})`);
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    _fgSmokeGrads[i] = grad;
  }
  return _fgSmokeGrads[i];
}
function getFgFringeGrad(ctx, i, p) {
  if (!_fgFringeGrads[i]) {
    const fringe = ctx.createRadialGradient(0, 0, 8, 0, 0, p.rx * 0.7);
    fringe.addColorStop(0, 'rgba(110, 231, 183, 0.07)');
    fringe.addColorStop(0.65, 'rgba(52, 211, 153, 0.02)');
    fringe.addColorStop(1, 'rgba(0, 0, 0, 0)');
    _fgFringeGrads[i] = fringe;
  }
  return _fgFringeGrads[i];
}

function drawSweepingSmokeFog(ctx, t, quality) {
  if (getQualityScale(quality) === 0) return;

  ctx.save();
  const fgSpeed = 38;
  const fgCycle = 1600;
  const fgShift = (t * fgSpeed) % fgCycle;

  for (let bx = -fgCycle; bx < WIDTH + fgCycle; bx += fgCycle) {
    for (let i = 0; i < FG_SMOKE_PUFFS.length; i++) {
      const p = FG_SMOKE_PUFFS[i];
      const px = bx + p.dx + fgShift;
      if (px < -300 || px > WIDTH + 300) continue;

      const waveY = p.dy + Math.sin(t * 1.3 + i * 0.9) * 7;
      const swell = 1.0 + 0.08 * Math.sin(t * 1.6 + i * 1.2);

      ctx.save();
      ctx.translate(px, waveY);
      ctx.scale(swell, swell);
      ctx.fillStyle = getFgSmokeGrad(ctx, i, p);
      ctx.beginPath();
      ctx.ellipse(0, 0, p.rx, p.ry, 0.04 * Math.sin(px * 0.01), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      if (getQualityScale(quality) === 1 && i % 2 === 0) {
        ctx.save();
        ctx.translate(px, waveY - 8);
        ctx.fillStyle = getFgFringeGrad(ctx, i, p);
        ctx.beginPath();
        ctx.ellipse(0, 0, p.rx * 0.7, p.ry * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }

  ctx.restore();
}

// Rare, slow-rolling dense fog bank that sweeps across the whole lane, on
// top of the map's permanent haze (drawBackgroundMistWisps/drawSweepingSmokeFog
// above) - Swamp's analogue to Sul's plasma lightning (backgroundSul.js's
// drawPlasmaLightning) and Human's rain gust (weather.js's getWeatherState):
// an irregular "weather event" with a deterministic per-mapSeed phase offset,
// synchronized with the battlefield simulation (see getSwampFogIntensity in js/engine/maps.js).
const FOG_BANK_BANDS = [
  { dy: 210, ry: 70 },
  { dy: 290, ry: 90 },
  { dy: 370, ry: 88 },
  { dy: 450, ry: 76 }
];
const FOG_BANK_BILLOW_ALPHA = 0.11;
const FOG_BANK_SHEET_ALPHA = 0.20;
const FOG_BANK_SPACING = 300;
const FOG_BANK_CYCLE = 1800;
const FOG_BANK_SPEED = 24;

// depthAlphaMul lets the same bank read as a hazier layer behind units and a
// denser one in front, matching the two-layer treatment the permanent mist
// already uses (drawBackgroundMistWisps vs drawSweepingSmokeFog).
//
// A translucent full-width sheet carries the bulk of the opacity so the bank
// reads as one continuous wall; the drifting radial billows on top are just
// texture/motion cues layered on that base - drawing the billows alone (as
// a first pass did) left visible gaps between them and read as a row of
// dots instead of fog.
let _fogBankSheetGrad = null;
function getFogBankSheetGrad(ctx) {
  if (!_fogBankSheetGrad) {
    _fogBankSheetGrad = ctx.createLinearGradient(0, LANE_Y - 20, 0, LANE_BOTTOM + 20);
    _fogBankSheetGrad.addColorStop(0, 'rgba(150, 178, 165, 0)');
    _fogBankSheetGrad.addColorStop(0.18, 'rgba(150, 178, 165, 1)');
    _fogBankSheetGrad.addColorStop(0.82, 'rgba(150, 178, 165, 1)');
    _fogBankSheetGrad.addColorStop(1, 'rgba(150, 178, 165, 0)');
  }
  return _fogBankSheetGrad;
}

let _fogBankBillowGrad = null;
function getFogBankBillowGrad(ctx) {
  if (!_fogBankBillowGrad) {
    _fogBankBillowGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    _fogBankBillowGrad.addColorStop(0, 'rgba(180, 204, 190, 1)');
    _fogBankBillowGrad.addColorStop(1, 'rgba(180, 204, 190, 0)');
  }
  return _fogBankBillowGrad;
}

function drawDenseFogBank(ctx, t, quality, intensity, depthAlphaMul) {
  const scale = getQualityScale(quality);
  if (scale === 0 || intensity <= 0.01) return;

  const topY = LANE_Y - 20;
  const bottomY = LANE_BOTTOM + 20;
  const sheetAlpha = FOG_BANK_SHEET_ALPHA * intensity * depthAlphaMul * scale;

  ctx.save();
  ctx.globalAlpha = sheetAlpha;
  ctx.fillStyle = getFogBankSheetGrad(ctx);
  ctx.fillRect(-40, topY, WIDTH + 80, bottomY - topY);
  ctx.restore();

  const shift = (t * FOG_BANK_SPEED) % FOG_BANK_CYCLE;
  const billowAlpha = FOG_BANK_BILLOW_ALPHA * intensity * depthAlphaMul * scale;
  const bGrad = getFogBankBillowGrad(ctx);
  for (const band of FOG_BANK_BANDS) {
    for (let bx = -FOG_BANK_CYCLE; bx < WIDTH + FOG_BANK_CYCLE; bx += FOG_BANK_SPACING) {
      const px = bx + shift;
      if (px < -400 || px > WIDTH + 400) continue;

      const waveY = band.dy + Math.sin(t * 0.5 + px * 0.004) * 14;
      const rx = FOG_BANK_SPACING * 0.95 * (0.85 + 0.3 * Math.sin(px * 0.007 + band.dy));

      ctx.save();
      ctx.translate(px, waveY);
      ctx.scale(rx, band.ry);
      ctx.globalAlpha = billowAlpha;
      ctx.fillStyle = bGrad;
      ctx.beginPath();
      ctx.arc(0, 0, 1, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}

// ══════════════════════════════════════════════════════════════════
// SWAMP BACKGROUND MIXIN (ATTACHED TO RENDERER.PROTOTYPE)
// ══════════════════════════════════════════════════════════════════

export const swampBackgroundMixin = {
  renderSwampStaticBackground(ctx, battlefield, customBounds = null) {
    const laneBottom = battlefield.laneY + battlefield.laneHeight;
    const bounds = resolveBounds(this, customBounds);
    const { visibleTop, visibleBottom, visibleLeft, visibleRight } = bounds;
    const bgWidth = visibleRight - visibleLeft;

    ctx.fillStyle = '#010507';
    ctx.fillRect(visibleLeft, visibleTop, bgWidth, visibleBottom - visibleTop);

    drawSkyStatic(ctx, bounds);
    drawMoonsStatic(ctx);
    drawDistantMushroomForest(ctx, bounds);
    drawSwampFloorStatic(ctx, bounds, laneBottom, this?.height || 540);
    drawWaterfallBasaltHill(ctx);

    if (this?.quality === 'low') {
      drawWaterfallStatic(ctx);
      drawHorizonFlora(ctx, 0);
    }

    drawMudTiles(ctx);
    drawMudRuts(ctx);
    drawEnrichedGroundDetailsStatic(ctx);
    drawReflectivePuddlesStatic(ctx);

    if (this?.quality === 'low') {
      drawPerspectiveGrassTexture(ctx, 0, 'low_baked');
    }

    // Letterbox veil: fade to exact page background color (#070b12) starting at laneBottom
    if (visibleBottom > laneBottom) {
      const veilTop = laneBottom;
      const veilBottom = Math.max(veilTop + 1, this?.height || 540);
      const veil = ctx.createLinearGradient(0, veilTop, 0, veilBottom);
      veil.addColorStop(0, 'rgba(7, 11, 18, 0)');
      veil.addColorStop(1, 'rgba(7, 11, 18, 1)');
      ctx.fillStyle = veil;
      ctx.fillRect(visibleLeft, veilTop, bgWidth, veilBottom - veilTop);
      if (visibleBottom > veilBottom) {
        ctx.fillStyle = 'rgb(7, 11, 18)';
        ctx.fillRect(visibleLeft, veilBottom, bgWidth, visibleBottom - veilBottom);
      }
    }
  },

  renderSwampDynamicBackground(ctx, battlefield, nowSec = null, customBounds = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;

    drawStarsDynamic(ctx, t, this.quality);
    drawMoonsDynamic(ctx, t);
    drawVolumetricGodRays(ctx, t, this.quality);
    // drawBackgroundMistWisps() runs once per frame in renderSwampWeatherBackground()
    // instead (right before units, matching its "weather depth layer" role) -
    // it used to also run here, compositing the same translucent mist twice per
    // frame (double alpha, double cost, no visual difference between the two calls).
    // 'low' already got its own frozen equivalent baked once into the
    // static background by renderSwampStaticBackground() above
    // (drawWaterfallStatic()) - this animated torrent (12+ gradients, ~14
    // wiggling strands, 8 foam tongues, every single frame) was drawn AND
    // fully overdrawing that bake on 'low' too, in violation of Paradigm 10
    // ("low: reiner statischer Blit, 0 zusätzliche CPU-Last", see
    // docs/PERFORMANCE_PARADIGMS.md). Medium still gets the thinned
    // (detailStep) animated version; only low skips straight to the bake.
    if (this.quality !== 'low') {
      drawWaterfallTorrent(ctx, t, this.quality);
    }
    drawHorizonFlora(ctx, t);
    drawDevilFlamethrowerSmoke(ctx, t, this.quality);
    drawReflectivePuddlesDynamic(ctx, t, this.quality);
    drawEnrichedGroundDetailsDynamic(ctx, t, this.quality);
    drawPerspectiveGrassTexture(ctx, t, this.quality);
    drawGroundMistWisps(ctx, t, this.quality);
  },

  renderSwampStaticForeground(ctx, battlefield, customBounds = null) {
    const bounds = resolveBounds(this, customBounds);
    drawCanopyAndBranchesStatic(ctx);
    drawFramingTrunkLeftStatic(ctx);
    drawFramingTrunkRightStatic(ctx);
    drawForegroundCornerFloraStatic(ctx);

    if (this?.quality === 'low') {
      drawTreeHollowGlow(ctx, 0);
      drawTrunkClimbingFern(ctx, 0);
      drawTrunkShelfFungi(ctx, 0, 'low');
      drawHangingVines(ctx, 0);
      drawCanopyLeavesStatic(ctx);
      drawForegroundCornerFloraDynamic(ctx, 0, 'low');
      drawAnimatedFern(ctx, 245, 524, 0.95, 0);
      drawAnimatedSporeBulrushes(ctx, 1335, 524, 1.0, 0, 'low');
      drawForegroundGrass(ctx, 0, 'low_baked');
      drawSwampSquirrel(ctx, 5.0, 'perch_static');
    }

    drawTrunkBaseFades(ctx, bounds, this?.height || 540);
  },

  renderSwampDynamicForeground(ctx, battlefield, nowSec = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;

    drawTreeHollowGlow(ctx, t);
    drawTrunkClimbingFern(ctx, t);
    drawTrunkShelfFungi(ctx, t, this.quality);
    drawHangingVines(ctx, t);
    drawCanopyLeavesAndFallingLeaves(ctx, t, this.quality);
    drawForegroundCornerFloraDynamic(ctx, t, this.quality);
    drawAnimatedFern(ctx, 245, 524, 0.95, t);
    drawAnimatedSporeBulrushes(ctx, 1335, 524, 1.0, t, this.quality);
    drawForegroundGrass(ctx, t, this.quality);
  },

  renderSwampForegroundCritters(ctx, battlefield, nowSec = null) {
    const t = nowSec !== null ? nowSec : performance.now() * 0.001;
    drawSwampSquirrel(ctx, t, this.quality);
  },

  // Analogous to backgroundSul.js's getSulWeatherState(): the occasional
  // dense-fog event's intensity (0-1), driven by deterministic mapSeed and duration.
  getSwampFogEventState(battlefield = null) {
    const t = battlefield?.stats?.duration !== undefined ? battlefield.stats.duration : (performance.now() * 0.001);
    const mapSeed = battlefield?.mapSeed ?? 0;
    const intensity = getSwampFogIntensity(t, mapSeed);
    return { t, intensity };
  },

  renderSwampWeatherBackground(ctx, battlefield) {
    const t = performance.now() * 0.001;
    drawBackgroundMistWisps(ctx, t, this.quality);
    const { intensity } = this.getSwampFogEventState(battlefield);
    if (intensity > 0.01) drawDenseFogBank(ctx, t, this.quality, intensity, 0.5);
  },

  renderSwampWeather(ctx, battlefield) {
    const t = performance.now() * 0.001;
    drawFloatingSpores(ctx, t, this.quality);
    drawSweepingSmokeFog(ctx, t, this.quality);
    const { intensity } = this.getSwampFogEventState(battlefield);
    if (intensity > 0.01) drawDenseFogBank(ctx, t, this.quality, intensity, 1.0);
  }
};
